import { generationBrowserContextSchema } from './quality-generation-browser.contract.js';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';
import {
  captureApiRequestAuthority,
  type ApiRequestAuthority,
} from '@/lib/api-request-authority.js';

import { CASE_OUTCOMES } from './quality-types.js';

import type {
  AnswerAuthFlightBody,
  AnswerInputBody,
  ApproveBody,
  ApplyRevisionResult,
  AtlassianTestResult,
  AuthFlight,
  AuthFlightState,
  Capability,
  CaseRevision,
  CaseSpec,
  CaseAdaptationResponse,
  SaveCaseAdaptationBody,
  ResetCaseAdaptationBody,
  SuiteExecutionContextResponse,
  SaveSuiteExecutionContextBody,
  CreateGeneratedCasePublicationBatchBody,
  CreateGeneratedCasePublicationBatchResponse,
  CreateEnvironmentBody,
  CreateExploreRunBody,
  CreateRunBody,
  DeviceLease,
  EnvironmentConfig,
  GeneratedCasePublication,
  GenerationContextResponse,
  GitHubSourceBody,
  GitHubTestResult,
  GitLabSourceBody,
  GitLabTestResult,
  DeviceTakeoverRequest,
  EnvironmentMode,
  HelpRequest,
  InputRequestState,
  OutcomeCorrectionBody,
  OutcomeCorrectionReceipt,
  BulkOutcomeReassessmentResult,
  CreateBulkOutcomeReassessmentBody,
  CreateOutcomeReassessmentBody,
  OutcomeReassessmentEligibility,
  OutcomeReassessmentReceipt,
  OutcomeReassessmentState,
  CaseOutcome,
  ValueInputKind,
  ValueInputRequest,
  WebNetworkCaptureOverride,
  CreateRegressionJobBody,
  Platform,
  Project,
  LaunchOptionsResponse,
  ProjectCredential,
  QualityCapabilitiesResponse,
  QualityMcpOperationName,
  RegressionJob,
  RegressionJobRow,
  RegressionRunConfig,
  RegressionRunSummary,
  ReportBundle,
  ReportPortalTestResult,
  ReportCases,
  ReportErrorDigest,
  ReportSummary,
  ReplayFinding,
  RequestChangesBody,
  RequestDescriptor,
  ResetAuthFlightBody,
  Run,
  RunDetail,
  RunLogLine,
  RunPreflightBody,
  RunPreflightResult,
  RunLaunchStatus,
  RunProposal,
  TakeoverCommandResult,
  TakeoverDevice,
  TakeoverState,
  TakeoverVerify,
  TestRailSectionTree,
  TestSource,
  TestSuite,
  TestSuiteCases,
  UpdateEnvironmentBody,
  UpdateProjectBody,
  UpdateRegressionJobBody,
  XrayFolder,
  ZephyrFolder,
  AdaptationContextRef,
  AdaptationContextState,
  SourceRef,
  LastGenerationRequestSnapshot,
  LocalCase,
  LocalCaseBody,
  LocalCasePage,
  LocalSuite,
} from './quality-types.js';

export type ExploratoryCredentialChoice =
  | { kind: 'environment'; credentialId?: string }
  | { kind: 'override'; credentialId: string }
  | { kind: 'anonymous' };

export type ExploratoryStoryVerdict = {
  storyRef: string;
  verdict: 'passed' | 'failed' | 'mixed' | 'not_assessed';
  explanation?: string;
};

export type ExploratoryManualRefs = {
  manualStoryRefs: string[];
  manualCaseRefs: string[];
};

/** A `test_steps_separated` entry on a new-case draft. */
export type ExploratoryTestStep = {
  content: string;
  expected?: string;
};

/**
 * A proposal to create a new case. `proposalType` is absent on every revision
 * saved before amendments existed, so the panel must treat a missing
 * `proposalType` as `'new_case'` rather than requiring the literal.
 */
export type ExploratoryNewCaseDraft = {
  proposalType?: 'new_case';
  title: string;
  precondition?: string;
  test_steps_separated?: ExploratoryTestStep[];
  expected_result?: string;
  custom_fields?: Record<string, unknown>;
  refs?: unknown;
  coverage?: unknown;
  citations?: unknown;
  [key: string]: unknown;
};

/**
 * A proposal to update an existing external case. Carries no `title` — the
 * change is expressed entirely as field-level `before`/`after` values against
 * the case identified by `target.caseRef`.
 */
export type ExploratoryAmendmentDraft = {
  proposalType: 'amendment';
  target: { caseRef: string };
  before: Record<string, unknown>;
  after: Record<string, unknown>;
};

export type ExploratoryDraftCase =
  ExploratoryNewCaseDraft | ExploratoryAmendmentDraft;

/** Narrows a draft to `ExploratoryAmendmentDraft`. A draft with no `proposalType` is a new-case draft. */
export function isExploratoryAmendment(
  draft: ExploratoryDraftCase,
): draft is ExploratoryAmendmentDraft {
  return draft.proposalType === 'amendment';
}

export type ExploratoryRevision = {
  id: string;
  artifact_id: string;
  revision: number;
  status: string;
  draft_cases: ExploratoryDraftCase[];
  story_verdicts: ExploratoryStoryVerdict[];
  manual_refs: ExploratoryManualRefs;
  explicit_links?: Record<string, unknown> | null;
  created_at: string;
};

export type ExploratoryPublicationAttempt = {
  id: string;
  revision_id: string;
  publication_identity: string;
  attempt_number: number;
  operation: 'create' | 'update';
  draft_case_index?: number | null;
  destination: {
    connector: string;
    projectRef?: string;
    containerRef?: string;
    caseRef?: string;
  };
  state:
    'pending' | 'in_flight' | 'succeeded' | 'failed' | 'ambiguous' | 'blocked';
  external_ids?: Record<string, unknown> | null;
  outcome_evidence?: Record<string, unknown> | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
};

export type ExploratoryArtifactRead = {
  artifact: {
    id: string;
    source_run_id: string;
    status: string;
    launch_snapshot?: Record<string, unknown> | null;
  };
  revisions: ExploratoryRevision[];
  publication_attempts: ExploratoryPublicationAttempt[];
};

const API_BASE = '/v1/quality';

/**
 * Error carrying the HTTP status so callers can branch on it — notably a 403
 * on `POST /runs` (no runner grant) which we surface as a friendly toast.
 */
export class QualityApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = 'QualityApiError';
  }
}

export async function responseError(
  res: Response,
  fallback: string,
): Promise<QualityApiError> {
  const text = await res.text().catch(() => '');
  let details: Readonly<Record<string, unknown>> | undefined;
  let message = text.trim() || `${fallback} (${res.status})`;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      details = parsed as Readonly<Record<string, unknown>>;
      const candidate = details.message;
      if (Array.isArray(candidate)) message = candidate.join('; ');
      else if (typeof candidate === 'string' && candidate.trim())
        message = candidate;
    }
  } catch {
    // Keep the bounded plain-text response as the message.
  }
  return new QualityApiError(res.status, message, details);
}

/**
 * Builds the request headers. The bearer is NOT one of them — `apiFetch`
 * attaches it from the renewal core, so every call here renews a near-expired
 * platform JWT before it goes out and retries once if the server rejects it.
 * Kept async so the 43 `await headers()` call sites stay untouched.
 */
// eslint-disable-next-line @typescript-eslint/require-await
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json' };
}

/** Extract a human-readable message from a Nest-style error body. */
async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
  } catch {
    /* not JSON */
  }
  return text.trim();
}

async function getJson<T>(
  path: string,
  fallback: string,
  signal?: AbortSignal,
): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    headers: await headers(),
    signal,
  });
  if (!res.ok) {
    throw new QualityApiError(res.status, await errorMessage(res, fallback));
  }
  return (await res.json()) as T;
}

// ── Tests + projects ───────────────────────────────────────────────────────

/**
 * Test suites visible to the user, scoped to the globally-selected workspace (`workspace_id`,
 * snake_case on the wire). `projectId` is still accepted for the legacy per-project filter; the
 * service takes either. `workspaceProjectId` names the project the caller resolved for that
 * workspace and rides along as `projectId`, so a workspace with several projects lists the one the
 * screen is showing; the service validates the pair.
 */
export async function fetchTestSuites(
  projectId?: string,
  workspaceId?: string,
  workspaceProjectId?: string,
  signal?: AbortSignal,
): Promise<TestSuite[]> {
  return getJson<TestSuite[]>(
    `/test-suites${testSuitesQuery(projectId, workspaceId, workspaceProjectId)}`,
    'Failed to load test suites',
    signal,
  );
}

/** The `?…` scope of the suite list routes, or '' when there is none. */
function testSuitesQuery(
  projectId?: string,
  workspaceId?: string,
  workspaceProjectId?: string,
): string {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (workspaceId) params.set('workspace_id', workspaceId);
  if (!projectId && workspaceId && workspaceProjectId)
    params.set('projectId', workspaceProjectId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * How far the suite list has come: source items read and expected so far. `sized` is true once a
 * source has said how many items it reads; before that every source is a one-item placeholder.
 */
export interface TestSuitesProgress {
  loaded: number;
  total: number;
  sized: boolean;
}

/** One event of the suite stream; `type` picks which other fields are set. */
interface TestSuitesStreamEvent {
  type?: string;
  loaded?: number;
  total?: number;
  sized?: boolean;
  suites?: TestSuite[];
  status?: number;
  message?: string;
}

/**
 * The same list as {@link fetchTestSuites}, read from the SSE route so the caller can show real
 * progress: `onProgress` gets every snapshot the service sends. A service without the stream route
 * (404) answers through the plain list, with no progress. EventSource cannot carry the bearer, so this
 * reads the stream through `apiFetch`.
 */
export async function streamTestSuites(
  projectId: string | undefined,
  workspaceId: string | undefined,
  workspaceProjectId: string | undefined,
  onProgress: (progress: TestSuitesProgress) => void,
  signal?: AbortSignal,
): Promise<TestSuite[]> {
  const scope = testSuitesQuery(projectId, workspaceId, workspaceProjectId);
  const res = await apiFetch(`${API_BASE}/test-suites/stream${scope}`, {
    headers: { ...(await headers()), accept: 'text/event-stream' },
    signal,
  });
  if (res.status === 404) {
    return fetchTestSuites(projectId, workspaceId, workspaceProjectId, signal);
  }
  if (!res.ok || !res.body) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to load test suites'),
    );
  }
  for await (const event of readTestSuitesStream(res.body)) {
    if (event.type === 'progress') {
      onProgress({
        loaded: event.loaded ?? 0,
        total: event.total ?? 0,
        sized: event.sized === true,
      });
    } else if (event.type === 'result') {
      return event.suites ?? [];
    } else if (event.type === 'error') {
      throw new QualityApiError(
        event.status ?? 500,
        event.message ?? 'Failed to load test suites',
      );
    }
  }
  throw new QualityApiError(500, 'Failed to load test suites');
}

/**
 * The `data:` payloads of an SSE body, parsed; keep-alive comments and malformed events are skipped.
 * Events may end in LF or CRLF, and a boundary may arrive split across chunks.
 */
async function* readTestSuitesStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<TestSuitesStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() ?? '';
    for (const block of blocks) {
      const data = block
        .split(/\r?\n/)
        .map((line) => line.trimStart())
        .find((line) => line.startsWith('data:'))
        ?.slice(5)
        .trim();
      if (!data) continue;
      try {
        yield JSON.parse(data) as TestSuitesStreamEvent;
      } catch {
        continue;
      }
    }
  }
}

/** Project-scoped targets for regression authoring; TestRail rows are case-library suites. */
export async function fetchRegressionTargets(
  projectId: string,
): Promise<TestSuite[]> {
  return getJson<TestSuite[]>(
    `/test-suites/regression-targets?projectId=${encodeURIComponent(projectId)}`,
    'Failed to load regression targets',
  );
}

/**
 * A test suite's content (name + cases with Markdown rich-text). `projectId` scopes the per-project
 * account used to fetch it (the run/cycle id carries no project context); passed from the Tests
 * browser so the right account — not the global env one — sources the cases. `source` picks the
 * provider (a TestRail run vs a Zephyr cycle) and must match the row the user clicked — the id alone
 * is ambiguous across sources. `sourceRef` carries the canonical source link and coordinates so
 * duplicate provider/container ids resolve to the clicked binding. Omitting projectId would 400 on
 * runs that live only in the project's account.
 */
export async function fetchTestSuiteCases(
  id: string,
  projectId?: string,
  source?: TestSource,
  workspaceId?: string,
  sourceRef?: SourceRef,
): Promise<TestSuiteCases> {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (source) params.set('source', source);
  if (workspaceId) params.set('workspace_id', workspaceId);
  if (sourceRef) params.set('source_ref', JSON.stringify(sourceRef));
  const qs = params.toString();
  return getJson<TestSuiteCases>(
    `/test-suites/${encodeURIComponent(id)}/cases${qs ? `?${qs}` : ''}`,
    'Failed to load test cases',
  );
}

/**
 * What the last-request endpoint answered: the caller's snapshot (null when there is none), or that
 * the service predates the endpoint, which is the only case the old project field may be read.
 */
export type LastGenerationRequestRead =
  | { supported: true; snapshot: LastGenerationRequestSnapshot | null }
  | { supported: false };

/** The caller's own last Generate request on a project, the form's prefill. */
export async function fetchLastGenerationRequest(
  projectId: string,
): Promise<LastGenerationRequestRead> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/last-generation-request`,
    { headers: await headers() },
  );
  if (res.status === 404) return { supported: false };
  if (!res.ok)
    throw await responseError(res, 'Failed to load the last Generate request');
  const body = (await res.json().catch(() => null)) as {
    last_generation_request?: LastGenerationRequestSnapshot | null;
  } | null;
  return { supported: true, snapshot: body?.last_generation_request ?? null };
}

/** Projects the user is granted (operator block stripped server-side). */
export async function fetchProjects(): Promise<Project[]> {
  return getJson<Project[]>('/projects', 'Failed to load projects');
}

/**
 * A single project. Members who can GET the project receive settings + capabilities (viewers get
 * the Teams webhook URL redacted); managers also get account metadata. The list endpoint still
 * masks that block for non-admins.
 * The Project settings panel fetches this so account/settings edits round-trip against fresh data.
 */
export async function fetchProject(id: string): Promise<Project> {
  return getJson<Project>(
    `/projects/${encodeURIComponent(id)}`,
    'Failed to load project',
  );
}

/** Authenticated service contract used to enable only fields the live server advertises. */
export async function fetchQualityCapabilities(): Promise<QualityCapabilitiesResponse> {
  return getJson<QualityCapabilitiesResponse>(
    '/mcp/capabilities',
    'Failed to load Quality capabilities',
  );
}

/** Safe, non-secret launch choices for one visible project. */
export async function fetchProjectLaunchOptions(
  projectId: string,
): Promise<LaunchOptionsResponse> {
  return getJson<LaunchOptionsResponse>(
    `/projects/${encodeURIComponent(projectId)}/launch-options`,
    'Failed to load launch options',
  );
}

/**
 * Checks the live v1 capability envelope before a caller renders or submits new fields. The
 * response is still inspected at runtime because an older or partially deployed service may return
 * a different JSON shape despite the compile-time response type.
 *
 * Answers only the question it asks — whether this operation accepts these fields. It deliberately
 * does not narrow the argument to `QualityCapabilitiesResponse`: it never reads `semantic_version`
 * or most of `transport`, so claiming the whole envelope was validated would let a later caller
 * read a field this check never saw.
 */
export function supportsQualityOperationFields(
  value: unknown,
  operationName: QualityMcpOperationName,
  fields: readonly string[],
): boolean {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<QualityCapabilitiesResponse>;
  if (
    response.service !== 'cti-quality-service' ||
    response.schema_version !== 1 ||
    response.transport?.authentication !== 'verified_bearer_per_request' ||
    !response.operations ||
    typeof response.operations !== 'object'
  ) {
    return false;
  }
  const operation = response.operations[operationName];
  if (!operation || !Array.isArray(operation.input_fields)) return false;
  const version = `${operation.operation_version}:${operation.input_schema_version}`;
  const supportedVersion =
    version === '1:1' ||
    ([
      'quality.run',
      'quality.get_suite_cases',
      'quality.rerun_current',
    ].includes(operationName) &&
      version === '2:2') ||
    (operationName === 'quality.restart_failed' && version === '3:3') ||
    (operationName === 'quality.preflight_run' && version === '1:2');
  if (!supportedVersion || operation.output_schema_version !== 1) return false;
  return fields.every((field) => operation.input_fields.includes(field));
}

/** True when the response carries this service's capability envelope AND a projection for this
 *  operation. An older or partially deployed service answers with a different shape, or with an
 *  envelope that does not describe the operation yet; either way discovery is missing for it, which
 *  is not the same as a refusal. */
function describesQualityOperation(
  value: unknown,
  operationName: QualityMcpOperationName,
): boolean {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<QualityCapabilitiesResponse>;
  return (
    response.service === 'cti-quality-service' &&
    response.schema_version === 1 &&
    Boolean(response.operations) &&
    typeof response.operations === 'object' &&
    Boolean(response.operations[operationName])
  );
}

/**
 * The live service describes this operation, but at a version this client does not speak. That is
 * not a legacy service: every request for the operation will be refused, so a caller must block and
 * offer a refresh rather than fall back to the established path.
 */
export function qualityOperationVersionUnsupported(
  value: unknown,
  operationName: QualityMcpOperationName,
): boolean {
  return (
    describesQualityOperation(value, operationName) &&
    !supportsQualityOperationFields(value, operationName, [])
  );
}

/** Re-read before mutation. An established payload survives a service that cannot describe the
 * operation at all — no discovery, an unrecognized shape, or an envelope without this projection —
 * but not one that describes it at a version this client does not speak. Any request beyond that
 * established set must have every field it sends advertised by the live service, the established
 * ones included, so a partially deployed service cannot silently drop one. */
async function assertCurrentRunContract(
  operation:
    | 'quality.run'
    | 'quality.restart_failed'
    | 'quality.rerun_all'
    | 'quality.rerun_current',
  body: object,
): Promise<void> {
  const keys = Object.entries(body)
    .filter(([, value]) => value !== undefined)
    .map(([key]) => key);
  const legacy =
    operation === 'quality.run'
      ? [
          'source',
          'projectId',
          'suite',
          'platform',
          'testRailRunId',
          'testRailSuiteId',
          'zephyrCycleKey',
          'zephyrFolderId',
          'azureProject',
          'azurePlanId',
          'azureSuiteId',
          'xrayProjectKey',
          'xrayTestPlanId',
          'xrayTestSetId',
          'cases',
          'caseCount',
          'parallelism',
          'model',
          'capability',
          'environmentName',
          'humanInteractionEnabled',
          'takeoverEnabled',
          /**
           * The launch key decides nothing about what a run executes — it only lets a retry find
           * the run the first attempt already created — so a launch carrying one describes exactly
           * the run it would have described without it. A service that ignores it behaves as it did
           * before, which is why it belongs here while driftConfirmationToken does not: dropping a
           * drift confirmation silently changes what executes.
           */
          'launchRequestKey',
        ]
      : [
          'environmentName',
          'humanInteractionEnabled',
          ...(operation === 'quality.restart_failed'
            ? ['caseIds', 'includeOutcomes']
            : []),
        ];
  const onlyLegacy = keys.every((key) => legacy.includes(key));
  const rejected =
    'The Quality service did not accept this launch request. Your settings were kept. Retrying will not change the answer, so report it and we will fix the service.';
  let capabilities: QualityCapabilitiesResponse;
  try {
    capabilities = await fetchQualityCapabilities();
  } catch (error) {
    if (error instanceof QualityApiError && error.status === 404 && onlyLegacy)
      return;
    throw error;
  }
  if (onlyLegacy) {
    if (!describesQualityOperation(capabilities, operation)) return;
    if (!supportsQualityOperationFields(capabilities, operation, []))
      throw new Error(rejected);
    return;
  }
  if (!describesQualityOperation(capabilities, operation))
    throw new Error(rejected);
  const fields = keys.map((key) =>
    key
      .replace(/^testRail/, 'testrail')
      .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
  );
  if (!supportsQualityOperationFields(capabilities, operation, fields))
    throw new Error(rejected);
}

/** New mutation fields must be advertised by the current service before any write. */
async function assertCurrentQualityFields(
  operation: QualityMcpOperationName,
  fields: readonly string[],
): Promise<void> {
  const capabilities = await fetchQualityCapabilities();
  if (!supportsQualityOperationFields(capabilities, operation, fields))
    throw new Error(
      'The Quality service does not support these settings. Your choices were kept; update the service before submitting.',
    );
}

/** Update a project's operator block (name, capabilities, settings, account overrides).
 *  Owner/editor (shell), platform admin, or `quality:write`. */
export async function updateProject(
  id: string,
  body: UpdateProjectBody,
): Promise<Project> {
  const res = await apiFetch(`${API_BASE}/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to save project settings'),
    );
  }
  return (await res.json()) as Project;
}

/**
 * Post a test message to a project's Teams webhook. `webhookUrl` checks the URL currently typed in the
 * form (no save needed); omitted ⇒ the server uses the project's stored webhook.
 * Owner/editor (shell), platform admin, or `quality:write`.
 */
export async function sendTeamsTest(
  id: string,
  webhookUrl?: string,
): Promise<{ ok: true }> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(id)}/teams-test`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(webhookUrl ? { webhookUrl } : {}),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to send Teams test'),
    );
  }
  return (await res.json()) as { ok: true };
}

/**
 * Checks the project's SAVED Report Portal setting: the quality service resolves the chosen
 * connection and asks Report Portal whether the key reaches the project. No body — unsaved edits
 * are not part of the contract, so the settings form asks for a save first.
 */
export async function sendReportPortalTest(
  id: string,
): Promise<ReportPortalTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(id)}/reportportal-test`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({}),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to test Report Portal'),
    );
  }
  return (await res.json()) as ReportPortalTestResult;
}

/** A source-link mapping (source + external project id). `(source, externalProjectId)` is unique. */
export interface SourceLinkBody {
  source: string;
  externalProjectId: string;
}

/** Add an external-source mapping to a project. Returns the updated project. Admin-only. */
export async function addSourceLink(
  id: string,
  body: SourceLinkBody,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(id)}/source-links`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to add source link'),
    );
  }
  return (await res.json()) as Project;
}

/** Remove a source-link mapping from a project. Returns the updated project. Admin-only. */
export async function deleteSourceLink(
  id: string,
  linkId: string,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(id)}/source-links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to remove source link'),
    );
  }
  return (await res.json()) as Project;
}

/** Create a new project environment (Block C, admin-only). Returns the whole (re-fetched) project. */
export async function createProjectEnvironment(
  projectId: string,
  body: CreateEnvironmentBody,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/environments`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to create environment'),
    );
  }
  return (await res.json()) as Project;
}

/** Edit an existing project environment by name (admin-only). Returns the whole (re-fetched) project. */
export async function updateProjectEnvironment(
  projectId: string,
  name: string,
  body: UpdateEnvironmentBody,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(name)}`,
    { method: 'PATCH', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to update environment'),
    );
  }
  return (await res.json()) as Project;
}

/** Delete a project environment by name (admin-only) — also wipes its Vault secret vars server-side.
 *  Returns the whole (re-fetched) project. */
export async function deleteProjectEnvironment(
  projectId: string,
  name: string,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(name)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to delete environment'),
    );
  }
  return (await res.json()) as Project;
}

/**
 * Active device leases for a farm project (AC7). Scoped by the device farm, not by
 * project — a lease held by another project on the farm is included, flagged
 * `belongsToThisProject: false`, so the panel can show it as the reason a run is parked instead of
 * an empty list. Owner/editor (shell), platform admin, or `quality:write`.
 */
export async function fetchDeviceLeases(
  projectId: string,
): Promise<DeviceLease[]> {
  const { leases } = await getJson<{ leases: DeviceLease[] }>(
    `/projects/${encodeURIComponent(projectId)}/device-leases`,
    'Failed to load device leases',
  );
  return leases;
}

/** Break a device lease this project holds on the device farm. Terminalizes the holding run and
 *  promotes the next FIFO waiter (server-side) — the caller just refetches the list. */
export async function breakDeviceLease(
  projectId: string,
  udid: string,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/device-leases/${encodeURIComponent(udid)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to break device lease'),
    );
  }
}

export interface ForceReleaseDeviceLeaseBody {
  udid: string;
  leaseId: string;
  ownerRunId: string;
}

/** Operator override bound to the selected lease; the server still enforces stopped ownership. */
export async function forceReleaseDeviceLease(
  projectId: string,
  body: ForceReleaseDeviceLeaseBody,
): Promise<void> {
  const response = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/device-leases/${encodeURIComponent(body.udid)}/force-release`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        lease_id: body.leaseId,
        owner_run_id: body.ownerRunId,
      }),
    },
    { retryUnauthorized: false },
  );
  if (!response.ok)
    throw new QualityApiError(
      response.status,
      await errorMessage(response, 'Failed to force release device lease'),
    );
}

export function useForceReleaseDeviceLease(
  projectId: string,
): UseMutationResult<void, Error, ForceReleaseDeviceLeaseBody> {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (body: ForceReleaseDeviceLeaseBody) =>
      forceReleaseDeviceLease(projectId, body),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: qualityKeys.deviceLeases(projectId),
      }),
  });
}

/** Upload (or replace) a shared file fixture. Multipart field name is `file`. */
export async function putSharedFileVar(
  projectId: string,
  key: string,
  file: File,
): Promise<Project> {
  const form = new FormData();
  form.append('file', file);
  const authHeaders = await headers();
  delete authHeaders['Content-Type'];
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/file-vars/shared/${encodeURIComponent(key)}`,
    { method: 'POST', headers: authHeaders, body: form },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to upload file value'),
    );
  }
  return (await res.json()) as Project;
}

/** Clear a shared file fixture (metadata + object). */
export async function clearSharedFileVar(
  projectId: string,
  key: string,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/file-vars/shared/${encodeURIComponent(key)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to clear file value'),
    );
  }
  return (await res.json()) as Project;
}

/** Upload (or replace) a per-environment file fixture. */
export async function putEnvFileVar(
  projectId: string,
  envName: string,
  key: string,
  file: File,
): Promise<Project> {
  const form = new FormData();
  form.append('file', file);
  const authHeaders = await headers();
  delete authHeaders['Content-Type'];
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(envName)}/file-vars/${encodeURIComponent(key)}`,
    { method: 'POST', headers: authHeaders, body: form },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to upload file value'),
    );
  }
  return (await res.json()) as Project;
}

/** Clear a per-environment file fixture. */
export async function clearEnvFileVar(
  projectId: string,
  envName: string,
  key: string,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(envName)}/file-vars/${encodeURIComponent(key)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to clear file value'),
    );
  }
  return (await res.json()) as Project;
}

// ── Credentials ────────────────────────────────────────────────────────────

/**
 * A project's login credentials, metadata only (the secret lives in Vault). Read-only here: managing
 * credentials is a workspace-scoped surface now — see `workspace-credentials.ts`. This list stays
 * because an environment's "Auto-login" binding still names a project credential
 * (`useProjectCredentials` feeds that picker).
 */
export async function fetchCredentials(
  projectId: string,
): Promise<ProjectCredential[]> {
  return getJson<ProjectCredential[]>(
    `/projects/${encodeURIComponent(projectId)}/credentials`,
    'Failed to load credentials',
  );
}

// Set a project's GitHub generation source (admin-only on the service: PATCH /projects/:id). Sends
// ONLY `accounts.github`, so name/capabilities/other accounts are untouched. The token is write-only
// — omit to keep the stored one, '' to clear it.
export async function updateProjectGitHubSource(
  projectId: string,
  body: GitHubSourceBody,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify({ accounts: { github: body } }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to save GitHub source'),
    );
  }
  return (await res.json()) as Project;
}

// Verify a project's GitHub connection (admin-only). Sends typed-but-unsaved repos/token (both fall
// back to the saved values server-side); the token is used only for the test and never returned.
export async function testProjectGitHubSource(
  projectId: string,
  body: { repos?: { repoUrl: string }[]; token?: string },
): Promise<GitHubTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/github-test`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'GitHub test failed'),
    );
  }
  return (await res.json()) as GitHubTestResult;
}

// Set a project's GitLab generation source (admin-only) — the GitLab twin of updateProjectGitHubSource.
export async function updateProjectGitLabSource(
  projectId: string,
  body: GitLabSourceBody,
): Promise<Project> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify({ accounts: { gitlab: body } }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to save GitLab source'),
    );
  }
  return (await res.json()) as Project;
}

// Verify a project's GitLab connection (admin-only) — the GitLab twin of testProjectGitHubSource.
export async function testProjectGitLabSource(
  projectId: string,
  body: { repos?: { repoUrl: string }[]; token?: string },
): Promise<GitLabTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/gitlab-test`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'GitLab test failed'),
    );
  }
  return (await res.json()) as GitLabTestResult;
}

// Verify a project's custom Jira/Confluence connection (admin-only). Sends typed-but-unsaved
// URL/email/token (each falls back to the saved value server-side); the token is used only for the
// test and never returned. `product` selects the /jira-test vs /confluence-test route.
export async function testProjectAtlassianAccount(
  projectId: string,
  product: 'jira' | 'confluence',
  body: { baseUrl?: string; username?: string; token?: string },
): Promise<AtlassianTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/${product}-test`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, `${product} test failed`),
    );
  }
  return (await res.json()) as AtlassianTestResult;
}

// ── Runs ─────────────────────────────────────────────────────────────────────

/** Visibility scope for the runs board: 'mine' = only runs I launched; 'all' = everything I may see. */
export type RunScope = 'mine' | 'all';

export async function fetchRuns(
  params: {
    projectId?: string;
    scope?: RunScope;
    workspaceId?: string;
    limit?: number;
    offset?: number;
    includeArchived?: boolean;
  } = {},
): Promise<Run[]> {
  const qs = new URLSearchParams();
  if (params.projectId) qs.set('projectId', params.projectId);
  if (params.workspaceId) qs.set('workspace_id', params.workspaceId);
  if (params.scope) qs.set('scope', params.scope);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.includeArchived) qs.set('include_archived', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getJson<Run[]>(`/runs${suffix}`, 'Failed to load runs');
}

/** Read the author's latest saved generation across the full project history. */
export function latestGenerationQueryOptions(projectId?: string) {
  return queryOptions({
    queryKey: ['quality', 'latest-generation', projectId],
    queryFn: async () => {
      const qs = new URLSearchParams({
        scope: 'mine',
        run_kind: 'generation',
        limit: '1',
      });
      if (projectId) qs.set('project_id', projectId);
      const page = await getJson<{ items: Run[] }>(
        `/runs/page?${qs}`,
        'Failed to load your latest generation',
      );
      return page.items[0] ?? null;
    },
    enabled: !!projectId,
    refetchInterval: 5_000,
  });
}

export interface RunDetailReadOptions {
  caseId?: string;
  correctionCursor?: string;
  deliveryCursor?: string;
  limit?: number;
}

export async function fetchRunDetail(
  id: string,
  options: RunDetailReadOptions = {},
): Promise<RunDetail> {
  const params = new URLSearchParams();
  if (options.caseId) params.set('caseId', options.caseId);
  if (options.correctionCursor)
    params.set('correctionCursor', options.correctionCursor);
  if (options.deliveryCursor)
    params.set('deliveryCursor', options.deliveryCursor);
  if (options.limit !== undefined)
    params.set('limit', String(Math.min(Math.max(options.limit, 1), 50)));
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return getJson<RunDetail>(
    `/runs/${encodeURIComponent(id)}${suffix}`,
    'Failed to load run',
  );
}

/** The worker log tail for a run (live Redis buffer, else the S3 archive) — seeds the live activity
 *  feed, which the SSE `log.appended` event then keeps fresh. The endpoint wraps the array as
 *  `{ lines }`, so unwrap it here. */
export async function fetchRunLogs(id: string): Promise<RunLogLine[]> {
  const { lines } = await getJson<{ lines: RunLogLine[] }>(
    `/runs/${encodeURIComponent(id)}/logs`,
    'Failed to load run logs',
  );
  return lines ?? [];
}

/** Query options for a run's log tail. `enabled` gates it to when the activity feed is shown (a run
 *  still in flight); refreshes are driven by the SSE `log.appended` event invalidating this key. */
export function runLogsQueryOptions(id: string | undefined, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.logs(id ?? ''),
    enabled: enabled && typeof id === 'string' && id.length > 0,
    queryFn: () => fetchRunLogs(id as string),
    staleTime: 2_000,
  });
}

/**
 * Query options for a finished generation run's Trace NDJSON. `relativeKey` is run-relative
 * (after stripping `runs/{id}/`). Missing artifacts resolve to null — the panel stays quiet.
 */
export function generationTraceQueryOptions(
  runId: string | undefined,
  relativeKey: string | null,
  enabled = true,
) {
  const normalizedKey = relativeKey?.trim() ?? '';
  return queryOptions({
    queryKey: qualityKeys.generationTrace(runId ?? '', normalizedKey),
    enabled:
      enabled &&
      typeof runId === 'string' &&
      runId.length > 0 &&
      normalizedKey.length > 0,
    queryFn: () => fetchGenerationTrace(runId as string, normalizedKey),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Query options for GET generation-context. Missing/404 resolves to null — Sources/Seed stay quiet.
 */
export function generationContextQueryOptions(
  runId: string | undefined,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualityKeys.generationContext(runId ?? ''),
    enabled: enabled && typeof runId === 'string' && runId.length > 0,
    queryFn: () => fetchGenerationContext(runId as string),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Query options for a structured seed-prompt JSON artifact. `relativeKey` is run-relative.
 * Missing artifacts resolve to null.
 */
export function structuredSeedQueryOptions(
  runId: string | undefined,
  relativeKey: string | null,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualityKeys.structuredSeed(runId ?? '', relativeKey ?? ''),
    enabled:
      enabled &&
      typeof runId === 'string' &&
      runId.length > 0 &&
      typeof relativeKey === 'string' &&
      relativeKey.length > 0,
    queryFn: () => fetchArtifactText(runId as string, relativeKey as string),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Query options for a bearer-authed text artifact (e.g. scrubbed seed-prompt.md).
 * Enable only when the user expands the optional rendered-prompt viewer.
 */
export function artifactTextQueryOptions(
  runId: string | undefined,
  relativeKey: string | null,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualityKeys.artifactText(runId ?? '', relativeKey ?? ''),
    enabled:
      enabled &&
      typeof runId === 'string' &&
      runId.length > 0 &&
      typeof relativeKey === 'string' &&
      relativeKey.length > 0,
    queryFn: () => fetchArtifactText(runId as string, relativeKey as string),
    staleTime: 60_000,
    retry: false,
  });
}

export async function createRun(body: CreateRunBody): Promise<Run> {
  await assertCurrentRunContract('quality.run', body);
  const res = await apiFetch(`${API_BASE}/runs`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await responseError(res, 'Failed to launch run');
  }
  return (await res.json()) as Run;
}

function adaptationPath(
  caseId: string,
  workspaceId: string,
  sourceRunId?: string,
  sourceRef?: SourceRef,
): string {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (sourceRunId) params.set('source_run_id', sourceRunId);
  if (sourceRef) params.set('source_ref', JSON.stringify(sourceRef));
  return `/case-adaptations/${encodeURIComponent(caseId)}?${params.toString()}`;
}

export function fetchCaseAdaptation(
  caseId: string,
  workspaceId: string,
  sourceRunId?: string,
  sourceRef?: SourceRef,
): Promise<CaseAdaptationResponse> {
  return getJson<CaseAdaptationResponse>(
    adaptationPath(caseId, workspaceId, sourceRunId, sourceRef),
    'Failed to load case adaptation',
  );
}

export async function saveCaseAdaptation(
  caseId: string,
  workspaceId: string,
  sourceRunId: string | undefined,
  body: SaveCaseAdaptationBody,
  sourceRef?: SourceRef,
): Promise<CaseAdaptationResponse> {
  const res = await apiFetch(
    `${API_BASE}${adaptationPath(caseId, workspaceId, sourceRunId, sourceRef ?? body.source_ref)}`,
    { method: 'PUT', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) throw await responseError(res, 'Failed to save adaptation');
  return (await res.json()) as CaseAdaptationResponse;
}

/** Publish the reviewed saved revision to its verified TestRail source. */
export async function publishCaseAdaptation(
  caseId: string,
  workspaceId: string,
  sourceRunId: string | undefined,
  body: ResetCaseAdaptationBody,
): Promise<CaseAdaptationResponse> {
  const path = adaptationPath(
    caseId,
    workspaceId,
    sourceRunId,
    body.source_ref,
  ).replace('?', '/publish?');
  const res = await apiFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({ ...body, approved: true }),
  });
  if (!res.ok) throw await responseError(res, 'Failed to publish to TestRail');
  return (await res.json()) as CaseAdaptationResponse;
}

export async function resetCaseAdaptation(
  caseId: string,
  workspaceId: string,
  sourceRunId: string | undefined,
  body: ResetCaseAdaptationBody,
  sourceRef?: SourceRef,
): Promise<CaseAdaptationResponse | null> {
  const res = await apiFetch(
    `${API_BASE}${adaptationPath(caseId, workspaceId, sourceRunId, sourceRef ?? body.source_ref)}`,
    { method: 'DELETE', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) throw await responseError(res, 'Failed to reset adaptation');
  if (res.status === 204) return null;
  return (await res.json()) as CaseAdaptationResponse;
}

function suiteContextPath(
  suiteId: string,
  workspaceId: string,
  sourceRef?: SourceRef,
  contextRef?: AdaptationContextRef | AdaptationContextState | null,
): string {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (sourceRef) params.set('source_ref', JSON.stringify(sourceRef));
  const resolvedContextRef =
    contextRef && 'state' in contextRef
      ? contextRef.state === 'folder' || contextRef.state === 'root'
        ? contextRef.context
        : undefined
      : contextRef;
  if (resolvedContextRef)
    params.set('context_ref', JSON.stringify(resolvedContextRef));
  return `/suite-execution-contexts/${encodeURIComponent(suiteId)}?${params.toString()}`;
}

export function fetchSuiteExecutionContext(
  suiteId: string,
  workspaceId: string,
  sourceRef?: SourceRef,
  contextRef?: AdaptationContextRef | AdaptationContextState | null,
): Promise<SuiteExecutionContextResponse> {
  return getJson<SuiteExecutionContextResponse>(
    suiteContextPath(suiteId, workspaceId, sourceRef, contextRef),
    'Failed to load suite instructions',
  );
}

export async function saveSuiteExecutionContext(
  suiteId: string,
  workspaceId: string,
  body: SaveSuiteExecutionContextBody,
  sourceRef?: SourceRef,
): Promise<SuiteExecutionContextResponse> {
  const res = await apiFetch(
    `${API_BASE}${suiteContextPath(suiteId, workspaceId, sourceRef, body.context_ref)}`,
    { method: 'PUT', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok)
    throw await responseError(res, 'Failed to save suite instructions');
  return (await res.json()) as SuiteExecutionContextResponse;
}

/** Path under the local test case store, scoped to the workspace whose project owns it. */
function localStorePath(
  path: string,
  workspaceId: string,
  query: Record<string, string> = {},
): string {
  const params = new URLSearchParams({ workspace_id: workspaceId, ...query });
  return `${path}?${params.toString()}`;
}

/** Sends one JSON write to the local store; the error keeps the service's conflict code. */
async function sendLocalStore<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body: object,
  fallback: string,
  authority?: ApiRequestAuthority,
): Promise<T> {
  authority?.assertCurrent();
  const res = await apiFetch(
    `${API_BASE}${path}`,
    {
      method,
      headers: await headers(),
      body: JSON.stringify(body),
    },
    {},
    authority,
  );
  if (!res.ok) throw await responseError(res, fallback);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Suites stored in Quality for the workspace's project. */
export async function fetchLocalSuites(
  workspaceId: string,
): Promise<LocalSuite[]> {
  const page = await getJson<{ items: LocalSuite[] }>(
    localStorePath('/local-suites', workspaceId),
    'Failed to load suites stored in Quality',
  );
  return page.items;
}

export function createLocalSuite(
  workspaceId: string,
  name: string,
  authority?: ApiRequestAuthority,
): Promise<LocalSuite> {
  return sendLocalStore(
    'POST',
    localStorePath('/local-suites', workspaceId),
    { name },
    'Failed to create the suite',
    authority,
  );
}

export function renameLocalSuite(
  workspaceId: string,
  suiteId: string,
  name: string,
): Promise<LocalSuite> {
  return sendLocalStore(
    'PATCH',
    localStorePath(`/local-suites/${encodeURIComponent(suiteId)}`, workspaceId),
    { name },
    'Failed to rename the suite',
  );
}

/** Deletes a suite and its cases; the service refuses when the count the user saw is stale. */
export function deleteLocalSuite(
  workspaceId: string,
  suiteId: string,
  expectedCaseCount: number,
): Promise<void> {
  return sendLocalStore(
    'DELETE',
    localStorePath(`/local-suites/${encodeURIComponent(suiteId)}`, workspaceId),
    { expected_case_count: expectedCaseCount },
    'Failed to delete the suite',
  );
}

/** Every case of a suite in position order, following the service's cursor to the last page. */
export async function fetchAllLocalCases(
  workspaceId: string,
  suiteId: string,
): Promise<LocalCase[]> {
  const cases: LocalCase[] = [];
  let cursor: string | null = null;
  do {
    const page: LocalCasePage = await getJson<LocalCasePage>(
      localStorePath(
        `/local-suites/${encodeURIComponent(suiteId)}/cases`,
        workspaceId,
        { limit: '200', ...(cursor ? { cursor } : {}) },
      ),
      'Failed to load test cases',
    );
    cases.push(...page.items);
    cursor = page.next_cursor;
  } while (cursor);
  return cases;
}

export async function createLocalCases(
  workspaceId: string,
  suiteId: string,
  cases: LocalCaseBody[],
): Promise<LocalCase[]> {
  const page = await sendLocalStore<{ items: LocalCase[] }>(
    'POST',
    localStorePath(
      `/local-suites/${encodeURIComponent(suiteId)}/cases`,
      workspaceId,
    ),
    { cases },
    'Failed to add the test cases',
  );
  return page.items;
}

/** Edits a case; a stale `expectedRevision` is refused with 409 `LOCAL_CASE_CHANGED`. */
export function updateLocalCase(
  workspaceId: string,
  caseId: string,
  expectedRevision: number,
  changes: Partial<LocalCaseBody>,
): Promise<LocalCase> {
  return sendLocalStore(
    'PATCH',
    localStorePath(`/local-cases/${encodeURIComponent(caseId)}`, workspaceId),
    { expected_revision: expectedRevision, ...changes },
    'Failed to save the test case',
  );
}

export function deleteLocalCase(
  workspaceId: string,
  caseId: string,
  expectedRevision: number,
): Promise<void> {
  return sendLocalStore(
    'DELETE',
    localStorePath(`/local-cases/${encodeURIComponent(caseId)}`, workspaceId),
    { expected_revision: expectedRevision },
    'Failed to delete the test case',
  );
}

/** Stores a new case order; `caseIds` must be the suite's full set. */
export async function reorderLocalCases(
  workspaceId: string,
  suiteId: string,
  caseIds: string[],
): Promise<LocalCase[]> {
  const page = await sendLocalStore<{ items: LocalCase[] }>(
    'POST',
    localStorePath(
      `/local-suites/${encodeURIComponent(suiteId)}/cases/reorder`,
      workspaceId,
    ),
    { case_ids: caseIds },
    'Failed to reorder the test cases',
  );
  return page.items;
}

export function fetchRunLaunch(id: string): Promise<RunLaunchStatus> {
  return getJson<RunLaunchStatus>(
    `/run-launches/${encodeURIComponent(id)}`,
    'Failed to load launch status',
  );
}

export async function reconcileRunLaunch(
  id: string,
  candidateId?: string,
): Promise<RunLaunchStatus> {
  const res = await apiFetch(
    `${API_BASE}/run-launches/${encodeURIComponent(id)}/reconcile`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(candidateId ? { candidate_id: candidateId } : {}),
    },
  );
  if (!res.ok) throw await responseError(res, 'Failed to reconcile launch');
  return (await res.json()) as RunLaunchStatus;
}

/** Path for the launch-preflight endpoint, kept in one place so a server path correction is a
 *  one-line change. */
function preflightPath(projectId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/runs/preflight`;
}

/**
 * Resolves a suite's cases server-side (the client never holds case bodies) and reports which
 * `{{tokens}}` will not resolve under the proposed execution settings. Canonical external-source
 * launches need the matching preview binding; legacy source warnings remain advisory.
 */
export async function preflightRun(
  projectId: string,
  body: RunPreflightBody,
): Promise<RunPreflightResult> {
  const res = await apiFetch(`${API_BASE}${preflightPath(projectId)}`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await responseError(res, 'Failed to preflight run');
  }
  return (await res.json()) as RunPreflightResult;
}

/** Launch an exploratory-testing run (the Explorer): drives the app, synthesizes test cases, and
 *  creates them in TestRail. Returns the run so the caller can route to the Runs board to watch it. */
export async function createExploreRun(
  body: CreateExploreRunBody,
): Promise<Run> {
  const res = await apiFetch(`${API_BASE}/runs/explore`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to start generation'),
    );
  }
  return (await res.json()) as Run;
}

/** Client-side input for a doc-synthesis run — the PDFs plus the scalar options. Sent as
 *  multipart/form-data (not a JSON body), so this is a UI shape, not a wire DTO. */
export interface CreateGenerateRunInput {
  files: File[];
  webNetworkCaptureMode?: WebNetworkCaptureOverride;
  projectId?: string;
  model?: string;
  maxCases?: number;
  instructions?: string;
  sectionId?: number;
}

/** Launch a doc-synthesis run: uploads PDF(s) and synthesizes test cases into TestRail. Sent as
 *  multipart/form-data. Returns the run so the caller can route to the Runs board to watch it. */
export async function createGenerateRun(
  input: CreateGenerateRunInput,
): Promise<Run> {
  const form = new FormData();
  for (const file of input.files) form.append('files', file);
  if (input.projectId) form.append('projectId', input.projectId);
  if (input.model) form.append('model', input.model);
  if (input.maxCases !== undefined)
    form.append('maxCases', String(input.maxCases));
  if (input.instructions) form.append('instructions', input.instructions);
  if (input.sectionId !== undefined)
    form.append('sectionId', String(input.sectionId));
  if (input.webNetworkCaptureMode)
    form.append('webNetworkCaptureMode', input.webNetworkCaptureMode);

  // Reuse the shared auth headers (keeps token logic in one place), but drop Content-Type so the
  // browser sets the multipart/form-data boundary itself.
  const authHeaders = await headers();
  delete authHeaders['Content-Type'];

  const res = await apiFetch(`${API_BASE}/runs/generate`, {
    method: 'POST',
    headers: authHeaders,
    body: form,
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to start generation'),
    );
  }
  return (await res.json()) as Run;
}

/** The per-run Azure Boards selection sent to the backend. A raw WIQL query is the scope; the
 *  organization + PAT are resolved server-side from the project's Azure account. Both fields
 *  optional for the minimal UI (an empty source still resolves against the project's default query). */
export interface AzureBoardsRunSourceInput {
  wiql?: string;
  includeComments?: boolean;
}

/** Client-side input for a combined-generation run — PDFs and/or a live app URL (+ optional login),
 *  any combination. Sent as multipart/form-data. Cases are proposed for review before TestRail. */
export interface CreateCombinedGenerateRunInput {
  /** Client-only continuation ownership; never serialized to Quality. */
  requestAuthority?: ApiRequestAuthority;
  generation_plan?: unknown;
  generationOriginConversationId?: string;
  generationOriginSnapshotRevision?: number;
  explorationMode?: 'custom' | 'named' | 'none';
  explorationEnvironmentName?: string;
  environmentMode?: 'default' | 'named' | 'none';
  environmentName?: string;
  scenarioIntent?: 'standalone' | 'e2e' | 'uat';
  humanInteractionEnabled?: boolean;
  takeoverEnabled?: boolean;
  files: File[];
  webNetworkCaptureMode?: WebNetworkCaptureOverride;
  projectId?: string;
  /** Explicitly include the bound project's Workspace knowledge source for this run. */
  useWorkspaceKnowledge?: boolean;
  sourceControlVersion?: 1;
  knowledgeInstructions?: string;
  knowledgeMaxRequests?: number;
  knowledgeChunksPerRequest?: number;
  useArtifacts?: boolean;
  artifactIds?: string[];
  useMemory?: boolean;
  memoryShared?: boolean;
  memoryPersonal?: boolean;
  model?: string;
  maxCases?: number;
  instructions?: string;
  /** TestRail output section (TestRail-sourced projects). Mutually exclusive with zephyrFolderId. */
  sectionId?: number;
  /** Zephyr output folder (Zephyr-sourced projects) — the section twin. A numeric Zephyr folder id
   *  (matches ZephyrFolder.id and CreateRunBody.zephyrFolderId). Cases land at the project root when
   *  omitted. Mutually exclusive with sectionId. */
  zephyrFolderId?: number;
  /** Existing TestRail section whose cases seed de-dup + style-matching — an INPUT source, distinct
   *  from the output-target sectionId. A TestRail section id; omit for the whole suite. Mutually
   *  exclusive with existingTestsFolderId. */
  existingTestsSectionId?: number;
  /** Existing Zephyr folder — the folder twin of existingTestsSectionId. A Zephyr folder id, modeled
   *  as a number to match ZephyrFolder.id and the output zephyrFolderId; stringified when appended to
   *  the multipart body. Omit for all folders. Mutually exclusive with existingTestsSectionId. */
  existingTestsFolderId?: number;
  /** The existing-tests source. Sent alone for a TestRail, Zephyr or Xray source, it means the whole
   *  container; beside it goes the one scope field that source uses. */
  existingTestsTms?: 'testrail' | 'zephyr' | 'xray' | 'azure' | 'own-tms';
  /** Existing Xray tests: a test-repository folder path. */
  existingTestsFolderPath?: string;
  /** Existing Azure DevOps tests: a Test Plan and a Test Suite, required together. */
  existingTestsAzurePlanId?: string;
  existingTestsAzureSuiteId?: string;
  /** Existing tests from a suite stored in Quality. */
  existingTestsLocalSuiteId?: string;
  /** Azure output target (Azure-sourced projects) — the ADO Test Plan + Test Suite cases land in.
   *  Both required together, no default-suite fallback. Mutually exclusive with sectionId and
   *  zephyrFolderId. */
  azurePlanId?: string;
  azureSuiteId?: string;
  /** Xray output target (Xray-sourced projects) — a test-repository folder path (matches
   *  XrayFolder.path), a Test Set, OR a Test Plan the generated cases are placed in. A Test Set or
   *  Test Plan is what makes the cases launchable: the Tests list offers containers, not folders.
   *  Mutually exclusive with each other and with sectionId, zephyrFolderId, and
   *  azurePlanId/azureSuiteId. */
  xrayFolderPath?: string;
  xrayTestSetId?: string;
  xrayTestPlanId?: string;
  /** Create the container instead of picking one: the name of a new Test Set / Test Plan to hold this
   *  run's cases. Joins the same exclusivity set as the three destinations above. */
  xrayNewTestSetSummary?: string;
  xrayNewTestPlanSummary?: string;
  /** Quality output target: the stored suite the approved cases are saved in. Exclusive with every
   *  TMS output target above. */
  localSuiteId?: string;
  /** Azure Boards input source: work items read via a WIQL query. Mirrors jiraSources/
   *  confluenceSources — per-run only, resolved server-side against the project's Azure account. */
  azureBoardsSources?: AzureBoardsRunSourceInput[];
  /** Web live-app source. Ignored for platform='mobile' — the app comes from the project's mobile
   *  capability instead. */
  appUrl?: string;
  /** Defaults to 'web'. Presence of 'mobile' is itself the live-app-source signal in place of
   *  appUrl (which mobile doesn't use). Requires the bound project to have a mobile capability +
   *  BrowserStack account. */
  platform?: Platform;
  /** Mobile device capability override (mobile only) — rarely needed; the project's stored
   *  capability is normally sufficient. */
  capability?: {
    device?: string;
    os?: string;
    appId?: string;
    appUrl?: string;
  };
  credentialId?: string;
  /** The GitHub repos to read for this run (each with an optional folder). When provided it is
   *  authoritative (exactly these are used, with the project's stored token); an empty array means
   *  no GitHub context. Omit to fall back to the project's saved repos. */
  githubRepos?: { repoUrl: string; branch?: string; subPath?: string }[];
  /** The GitLab projects to read for this run — the GitLab twin of githubRepos. */
  gitlabRepos?: { repoUrl: string; branch?: string; subPath?: string }[];
  /** The Jira sources to read for this run. Uses the launching user's OAuth token (resolved
   *  server-side from the connector), so it is per-run only and never falls back to project config. */
  jiraSources?: JiraRunSourceInput[];
  /** The Confluence sources to read for this run — the Confluence twin of jiraSources. */
  confluenceSources?: ConfluenceRunSourceInput[];
  /** What to read from Jira in the user's own words, resolved server-side into jiraSources. Sending
   *  both this and jiraSources is rejected. */
  jiraPrompt?: string;
  /** The Confluence twin of jiraPrompt; one prompt may resolve to several spaces. */
  confluencePrompt?: string;
  /** Whether the sources the Jira prompt resolves to should carry comments. In query mode this
   *  rides on each source entry; a prompt has no entry to put it on. */
  jiraPromptIncludeComments?: boolean;
  /** The Confluence twin of jiraPromptIncludeComments. */
  confluencePromptIncludeComments?: boolean;
}

/** Launch a combined-generation run (the unified flow): synthesize from documents AND/OR a live app,
 *  then review the proposed cases before they're written to TestRail. */
export async function createCombinedGenerateRun(
  input: CreateCombinedGenerateRunInput,
): Promise<Run> {
  const authority =
    input.requestAuthority ??
    (input.generation_plan ? captureApiRequestAuthority() : undefined);
  try {
    authority?.assertCurrent();
    /**
     * sectionId (TestRail), zephyrFolderId (Zephyr), azurePlanId+azureSuiteId (Azure), and
     * xrayFolderPath/xrayTestSetId (Xray) are mutually exclusive output targets — guard here so a
     * caller bug fails loudly instead of sending more than one and hitting a hard-to-debug 4xx. Azure
     * only counts as a target once BOTH ids are present (they are required together, no default-suite
     * fallback) — a lone id is an incomplete caller state, not a competing output target.
     * Trim + require non-empty: an empty/whitespace plan or suite id is not a real output target, so
     * it must not count toward mutual exclusivity nor reach the backend.
     */
    const azurePlanId = input.azurePlanId?.trim();
    const azureSuiteId = input.azureSuiteId?.trim();
    const azureOutput = Boolean(azurePlanId) && Boolean(azureSuiteId);
    const xrayFolderPath = input.xrayFolderPath?.trim();
    const xrayTestSetId = input.xrayTestSetId?.trim();
    const xrayTestPlanId = input.xrayTestPlanId?.trim();
    const xrayNewTestSetSummary = input.xrayNewTestSetSummary?.trim();
    const xrayNewTestPlanSummary = input.xrayNewTestPlanSummary?.trim();
    const xrayTargets = [
      xrayFolderPath,
      xrayTestSetId,
      xrayTestPlanId,
      xrayNewTestSetSummary,
      xrayNewTestPlanSummary,
    ].filter(Boolean);
    if (xrayTargets.length > 1) {
      throw new Error(
        'the Xray folder, Test Set, Test Plan and new-container names are mutually exclusive output targets',
      );
    }
    const localSuiteId = input.localSuiteId?.trim();
    const outputTargetCount = [
      input.sectionId !== undefined,
      input.zephyrFolderId !== undefined,
      azureOutput,
      xrayTargets.length > 0,
      Boolean(localSuiteId),
    ].filter(Boolean).length;
    if (outputTargetCount > 1) {
      throw new Error(
        'sectionId, zephyrFolderId, azurePlanId/azureSuiteId, xrayFolderPath/xrayTestSetId/' +
          'xrayTestPlanId/xrayNewTestSetSummary/xrayNewTestPlanSummary, and localSuiteId are ' +
          'mutually exclusive generate-output targets',
      );
    }
    if (
      Boolean(input.existingTestsAzurePlanId) !==
      Boolean(input.existingTestsAzureSuiteId)
    ) {
      throw new Error(
        'An Azure DevOps existing-tests plan and suite must be sent together.',
      );
    }
    /** The existing-tests scopes are mutually exclusive; guard so a caller bug fails loudly. */
    const existingTestsScopeCount = [
      input.existingTestsSectionId !== undefined,
      input.existingTestsFolderId !== undefined,
      Boolean(input.existingTestsFolderPath),
      Boolean(
        input.existingTestsAzurePlanId || input.existingTestsAzureSuiteId,
      ),
      Boolean(input.existingTestsLocalSuiteId),
    ].filter(Boolean).length;
    if (existingTestsScopeCount > 1) {
      throw new Error(
        'existingTestsSectionId, existingTestsFolderId, existingTestsFolderPath, ' +
          'existingTestsAzurePlanId/existingTestsAzureSuiteId and existingTestsLocalSuiteId are ' +
          'mutually exclusive existing-tests sources',
      );
    }
    /** A service that predates a field drops it silently, so each of these is advertised first. */
    const negotiatedFields = (
      [
        'scenarioIntent',
        'environmentMode',
        'environmentName',
        'explorationMode',
        'explorationEnvironmentName',
        'sourceControlVersion',
        'knowledgeInstructions',
        'knowledgeMaxRequests',
        'knowledgeChunksPerRequest',
        'useArtifacts',
        'artifactIds',
        'useMemory',
        'memoryShared',
        'memoryPersonal',
        'humanInteractionEnabled',
        'takeoverEnabled',
        'localSuiteId',
        'existingTestsTms',
        'existingTestsFolderPath',
        'existingTestsAzurePlanId',
        'existingTestsAzureSuiteId',
        'existingTestsLocalSuiteId',
      ] as const
    )
      .filter((key) => input[key] !== undefined)
      .map((key) =>
        key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
      );
    if (negotiatedFields.length)
      await assertCurrentQualityFields(
        'quality.generate_combined',
        negotiatedFields,
      );
    authority?.assertCurrent();
    if (input.generation_plan) {
      const capability = await generationPlanRequest<{
        generation_plan_version?: unknown;
      } | null>('/generation/capabilities', undefined, authority);
      authority?.assertCurrent();
      if (capability?.generation_plan_version !== 1)
        throw new Error(
          'The connected service does not support generation plan version 1. Keep this setup and try again after deployment.',
        );
    }
    const form = new FormData();
    if (
      input.generationOriginConversationId !== undefined ||
      input.generationOriginSnapshotRevision !== undefined
    ) {
      if (
        !input.generationOriginConversationId ||
        !Number.isSafeInteger(input.generationOriginSnapshotRevision) ||
        input.generationOriginSnapshotRevision! < 0
      )
        throw new Error(
          'A generation origin requires its exact conversation and snapshot revision.',
        );
      form.append(
        'generationOriginConversationId',
        input.generationOriginConversationId,
      );
      form.append(
        'generationOriginSnapshotRevision',
        String(input.generationOriginSnapshotRevision),
      );
    }
    if (input.generation_plan)
      form.append('generation_plan', JSON.stringify(input.generation_plan));
    form.append('generationReviewVersion', '1');
    if (input.environmentMode)
      form.append('environmentMode', input.environmentMode);
    if (input.environmentName)
      form.append('environmentName', input.environmentName);
    if (input.explorationMode)
      form.append('exploration_mode', input.explorationMode);
    if (input.explorationEnvironmentName)
      form.append(
        'exploration_environment_name',
        input.explorationEnvironmentName,
      );
    for (const file of input.files) form.append('files', file);
    if (input.projectId) form.append('projectId', input.projectId);
    if (input.useWorkspaceKnowledge === true) {
      form.append('useWorkspaceKnowledge', 'true');
      form.append('use_workspace_knowledge', 'true');
    }
    if (input.sourceControlVersion)
      form.append('source_control_version', String(input.sourceControlVersion));
    for (const field of [
      'knowledgeInstructions',
      'knowledgeMaxRequests',
      'knowledgeChunksPerRequest',
      'useArtifacts',
      'useMemory',
      'memoryShared',
      'memoryPersonal',
    ] as const) {
      if (input[field] !== undefined)
        form.append(
          (
            {
              knowledgeInstructions: 'knowledge_instructions',
              knowledgeMaxRequests: 'knowledge_max_requests',
              knowledgeChunksPerRequest: 'knowledge_chunks_per_request',
              useArtifacts: 'use_artifacts',
              useMemory: 'use_memory',
              memoryShared: 'memory_shared',
              memoryPersonal: 'memory_personal',
            } as const
          )[field],
          String(input[field]),
        );
    }
    if (input.artifactIds !== undefined)
      form.append('artifact_ids', JSON.stringify(input.artifactIds));
    if (input.humanInteractionEnabled !== undefined)
      form.append(
        'humanInteractionEnabled',
        String(input.humanInteractionEnabled),
      );
    if (input.takeoverEnabled !== undefined)
      form.append('takeoverEnabled', String(input.takeoverEnabled));
    if (input.model) form.append('model', input.model);
    if (input.maxCases !== undefined)
      form.append('maxCases', String(input.maxCases));
    if (input.scenarioIntent)
      form.append('scenarioIntent', input.scenarioIntent);
    if (input.instructions) form.append('instructions', input.instructions);
    if (input.sectionId !== undefined)
      form.append('sectionId', String(input.sectionId));
    if (input.zephyrFolderId !== undefined)
      form.append('zephyrFolderId', String(input.zephyrFolderId));
    if (input.existingTestsSectionId !== undefined)
      form.append(
        'existingTestsSectionId',
        String(input.existingTestsSectionId),
      );
    if (input.existingTestsFolderId !== undefined)
      form.append('existingTestsFolderId', String(input.existingTestsFolderId));
    if (input.existingTestsTms)
      form.append('existingTestsTms', input.existingTestsTms);
    if (input.existingTestsFolderPath)
      form.append('existingTestsFolderPath', input.existingTestsFolderPath);
    if (input.existingTestsAzurePlanId && input.existingTestsAzureSuiteId) {
      form.append('existingTestsAzurePlanId', input.existingTestsAzurePlanId);
      form.append('existingTestsAzureSuiteId', input.existingTestsAzureSuiteId);
    }
    if (input.existingTestsLocalSuiteId)
      form.append('existingTestsLocalSuiteId', input.existingTestsLocalSuiteId);
    // Azure output is all-or-nothing (Test Plans has no default suite) — send plan+suite together or
    // neither, matching the backend guard, so a lone id never reaches the service.
    if (azureOutput) {
      form.append('azurePlanId', azurePlanId as string);
      form.append('azureSuiteId', azureSuiteId as string);
    }
    if (xrayFolderPath) form.append('xrayFolderPath', xrayFolderPath);
    if (xrayTestSetId) form.append('xrayTestSetId', xrayTestSetId);
    if (xrayTestPlanId) form.append('xrayTestPlanId', xrayTestPlanId);
    if (xrayNewTestSetSummary)
      form.append('xrayNewTestSetSummary', xrayNewTestSetSummary);
    if (xrayNewTestPlanSummary)
      form.append('xrayNewTestPlanSummary', xrayNewTestPlanSummary);
    if (localSuiteId) form.append('localSuiteId', localSuiteId);
    if (input.azureBoardsSources)
      form.append(
        'azureBoardsSources',
        JSON.stringify(input.azureBoardsSources),
      );
    if (input.platform) form.append('platform', input.platform);
    // capability travels as a JSON string — multipart/form-data has no clean nested-object shape (the
    // service parses it back with parseCapabilityJson, same as githubRepos/jiraSources).
    // capability is mobile-only; only send it for mobile so a web run never carries an ambiguous payload.
    if (input.capability && input.platform === 'mobile')
      form.append('capability', JSON.stringify(input.capability));
    // appUrl is a web-only source; omit it for mobile so the wire contract is unambiguous (mobile
    // launches from the project's capability, and the service ignores appUrl for mobile regardless).
    if (input.appUrl && input.platform !== 'mobile')
      form.append('appUrl', input.appUrl);
    if (input.credentialId) form.append('credentialId', input.credentialId);
    if (input.githubRepos)
      form.append('githubRepos', JSON.stringify(input.githubRepos));
    if (input.gitlabRepos)
      form.append('gitlabRepos', JSON.stringify(input.gitlabRepos));
    if (input.jiraSources)
      form.append('jiraSources', JSON.stringify(input.jiraSources));
    if (input.confluenceSources)
      form.append('confluenceSources', JSON.stringify(input.confluenceSources));
    if (input.jiraPrompt) {
      form.append('jiraPrompt', input.jiraPrompt);
      form.append(
        'jiraPromptIncludeComments',
        String(input.jiraPromptIncludeComments === true),
      );
    }
    if (input.confluencePrompt) {
      form.append('confluencePrompt', input.confluencePrompt);
      form.append(
        'confluencePromptIncludeComments',
        String(input.confluencePromptIncludeComments === true),
      );
    }
    if (input.webNetworkCaptureMode && input.platform !== 'mobile')
      form.append('webNetworkCaptureMode', input.webNetworkCaptureMode);

    const authHeaders = await headers();
    authority?.assertCurrent();
    delete authHeaders['Content-Type'];

    const res = await apiFetch(
      `${API_BASE}/runs/generate-combined`,
      {
        method: 'POST',
        headers: authHeaders,
        body: form,
      },
      {},
      authority,
    );
    if (!res.ok) throw await responseError(res, 'Failed to start generation');
    return (await res.json()) as Run;
  } finally {
    if (!input.requestAuthority) authority?.release();
  }
}

export interface ExploratoryScopePreviewSource {
  ref: string;
  connector: ExploratoryTmsConnector;
  load_status: 'loaded' | 'unresolved';
  title?: string;
  failure?: { code: string; message: string };
}

export interface ExploratoryScopePreview {
  sources: ExploratoryScopePreviewSource[];
}

/** Reads selected exploratory sources without creating a run or publishing cases. */
export async function previewExploratoryScope(input: {
  projectId: string;
  jiraStoryRefs?: string[];
  existingCaseRefs?: ExploratoryExistingCaseRef[];
}): Promise<ExploratoryScopePreview> {
  const response = await apiFetch(
    `${API_BASE}/runs/exploratory-scope-preview`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        project_id: input.projectId,
        ...(input.jiraStoryRefs
          ? { jira_story_refs: input.jiraStoryRefs }
          : {}),
        ...(input.existingCaseRefs
          ? {
              existing_case_refs: input.existingCaseRefs.map(
                ({ caseRef, connector }) => ({ case_ref: caseRef, connector }),
              ),
            }
          : {}),
      }),
    },
  );
  if (!response.ok)
    throw new QualityApiError(
      response.status,
      await errorMessage(response, 'Could not check these references.'),
    );
  return (await response.json()) as ExploratoryScopePreview;
}

/** The TMS connectors an existing-case ref (or a publication destination) can name. */
export type ExploratoryTmsConnector =
  'testrail' | 'zephyr' | 'jira' | 'azure' | 'xray';

/**
 * An existing TMS case the user selected for impact analysis. `connector` is required — a bare
 * ref is unresolvable when a project has more than one TMS source configured (e.g. TestRail AND
 * Zephyr), so the UI must resolve one connector per launch rather than guessing.
 */
export type ExploratoryExistingCaseRef = {
  caseRef: string;
  connector: ExploratoryTmsConnector;
};

interface ExploratoryRunInputBase {
  humanInteractionEnabled?: boolean;
  takeoverEnabled?: boolean;
  webNetworkCaptureMode?: WebNetworkCaptureOverride;
  model?: string;
  instructions?: string;
  credentialId?: string;
  /** Time limit for the session, in minutes (1–90). Omitted ⇒ the worker's default. */
  maxMinutes?: number;
  /** Optional reference documents (PDF/Word/Excel/text) — an expected-behaviour oracle the browse is compared against. */
  files?: File[];
  /**
   * Optional Jira story refs to scope the session for impact analysis. Never
   * required — an ordinary exploratory run works exactly the same without it.
   */
  jiraStoryRefs?: string[];
  /**
   * Optional existing TMS case refs the user selected for impact analysis.
   * Never required, and never a claim of automatic linkage — see the JSDoc
   * on {@link createExploratoryRun}.
   */
  existingCaseRefs?: ExploratoryExistingCaseRef[];
}

/** Web exploratory run: browse a live URL. `appUrl` is required; a capability is meaningless here. */
export interface CreateWebExploratoryRunInput extends ExploratoryRunInputBase {
  platform?: 'web';
  appUrl?: string;
  projectId?: string;
  capability?: never;
  launchSource?: 'environment' | 'customUrl';
  environmentName?: string;
  customUrl?: string;
  credentialMode?: ExploratoryCredentialChoice['kind'];
}

/** Mobile exploratory run: launch the project's mobile app on BrowserStack. Requires `projectId`
 *  (the mobile capability + BrowserStack account are resolved from the bound project); `appUrl` is
 *  not accepted — the app comes from the resolved capability. */
export interface CreateMobileExploratoryRunInput extends ExploratoryRunInputBase {
  platform: 'mobile';
  projectId: string;
  appUrl?: never;
  /** Mobile device capability override — rarely needed; the project's stored capability normally
   *  suffices. */
  capability?: {
    device?: string;
    os?: string;
    appId?: string;
    appUrl?: string;
  };
  launchSource?: never;
  environmentName?: never;
  customUrl?: never;
  credentialMode?: ExploratoryCredentialChoice['kind'];
}

/** Discriminated on `platform` so the wire contract is enforced at compile time: web requires a URL,
 *  mobile requires a project and forbids a URL. */
export type CreateExploratoryRunInput =
  CreateWebExploratoryRunInput | CreateMobileExploratoryRunInput;

/**
 * Launch a standalone exploratory-testing session: browse a live app and produce a report plus
 * completed-session review artifacts. multipart/form-data lets reference documents ride along.
 *
 * `jiraStoryRefs` and `existingCaseRefs` carry the session's optional scope (Jira stories and
 * existing TMS cases the user picked for impact analysis).
 */
export async function createExploratoryRun(
  input: CreateExploratoryRunInput,
): Promise<Run> {
  const appUrl = input.appUrl?.trim();
  const environmentName = input.environmentName?.trim();
  const customUrl = input.customUrl?.trim();
  const credentialId = input.credentialId?.trim();
  if (
    input.platform === 'mobile' &&
    (input.launchSource !== undefined ||
      input.environmentName !== undefined ||
      input.customUrl !== undefined ||
      input.appUrl !== undefined)
  ) {
    throw new Error(
      'A mobile exploratory launch uses project capability and forbids web launch-source fields.',
    );
  }
  if (
    !input.launchSource &&
    (input.environmentName !== undefined || input.customUrl !== undefined)
  ) {
    throw new Error(
      'Explicit environmentName/customUrl fields require launchSource.',
    );
  }
  if (input.platform !== 'mobile' && !input.launchSource && !appUrl) {
    throw new Error('A web exploratory launch requires an appUrl.');
  }
  if (input.credentialMode === 'override' && !credentialId) {
    throw new Error(
      'An override credential mode requires a non-empty credentialId.',
    );
  }
  if (input.credentialMode === 'anonymous' && credentialId) {
    throw new Error('Anonymous credential mode forbids credentialId.');
  }
  if (input.launchSource === 'environment') {
    if (!environmentName || input.customUrl || input.appUrl)
      throw new Error(
        'An environment exploratory launch requires environmentName and forbids customUrl/appUrl.',
      );
  }
  if (input.launchSource === 'customUrl') {
    if (!customUrl || input.environmentName || input.appUrl)
      throw new Error(
        'A custom URL exploratory launch requires customUrl and forbids environmentName/appUrl.',
      );
  }
  const negotiatedFields = (
    ['humanInteractionEnabled', 'takeoverEnabled'] as const
  )
    .filter((key) => input[key] !== undefined)
    .map((key) =>
      key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
    );
  if (negotiatedFields.length)
    await assertCurrentQualityFields('quality.exploratory', negotiatedFields);
  const form = new FormData();
  for (const file of input.files ?? []) form.append('files', file);
  // appUrl is a web-only source — the discriminated CreateExploratoryRunInput forbids it on the
  // mobile variant, so no platform sub-check is needed here.
  // `appUrl` is the legacy branch only. Explicit source branches deliberately
  // use their own fields so the service's discriminated DTO never sees mixed input.
  if (appUrl && !input.launchSource) form.append('appUrl', appUrl);
  if (input.platform) form.append('platform', input.platform);
  // capability travels as a JSON string — multipart/form-data has no clean nested-object shape (the
  // service parses it back with parseCapabilityJson, same as githubRepos/jiraSources elsewhere). The
  // union only allows capability on the mobile variant, so a web run can never carry one.
  if (input.capability)
    form.append('capability', JSON.stringify(input.capability));
  if (input.projectId) form.append('projectId', input.projectId);
  if (input.model) form.append('model', input.model);
  if (input.instructions) form.append('instructions', input.instructions);
  if (credentialId) form.append('credentialId', credentialId);
  if (input.launchSource) form.append('launchSource', input.launchSource);
  if (environmentName) form.append('environmentName', environmentName);
  if (customUrl) form.append('customUrl', customUrl);
  if (input.credentialMode) form.append('credentialMode', input.credentialMode);
  if (input.maxMinutes !== undefined)
    form.append('maxMinutes', String(input.maxMinutes));
  if (input.humanInteractionEnabled !== undefined)
    form.append(
      'humanInteractionEnabled',
      String(input.humanInteractionEnabled),
    );
  if (input.takeoverEnabled !== undefined)
    form.append('takeoverEnabled', String(input.takeoverEnabled));
  // Optional scope for impact analysis (R9) — travels as a JSON string, same shape as
  // capability/jiraSources above. Never required: an ordinary run with neither field set behaves
  // exactly as before (R2/R8).
  if (input.jiraStoryRefs?.length)
    form.append('jiraStoryRefs', JSON.stringify(input.jiraStoryRefs));
  if (input.existingCaseRefs?.length)
    form.append('existingCaseRefs', JSON.stringify(input.existingCaseRefs));
  if (input.webNetworkCaptureMode && input.platform !== 'mobile')
    form.append('webNetworkCaptureMode', input.webNetworkCaptureMode);

  const authHeaders = await headers();
  delete authHeaders['Content-Type'];

  const res = await apiFetch(`${API_BASE}/runs/exploratory`, {
    method: 'POST',
    headers: authHeaders,
    body: form,
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to start exploratory session'),
    );
  }
  return (await res.json()) as Run;
}

/** Proposed-case version history for a generation run (newest first). */
export async function fetchRunProposals(id: string): Promise<RunProposal[]> {
  const data = await getJson<unknown>(
    `/runs/${encodeURIComponent(id)}/proposals`,
    'Failed to load proposals',
  );
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(
      'Invalid quality proposals response: proposals must be an array.',
    );
  }
  const response = data as Record<string, unknown>;
  if (!Array.isArray(response.proposals)) {
    throw new Error(
      'Invalid quality proposals response: proposals must be an array.',
    );
  }
  return response.proposals.map((value, index) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error(
        `Invalid quality proposal response: proposal at index ${index} must be an object.`,
      );
    }
    const row = value as Record<string, unknown>;
    const hasCanonicalGenerationMeta = Object.prototype.hasOwnProperty.call(
      row,
      'generationMeta',
    );
    const generationMeta = hasCanonicalGenerationMeta
      ? row.generationMeta
      : row.generation_meta;
    const { generation_meta: _legacyGenerationMeta, ...canonical } = row;
    return {
      ...canonical,
      ...(hasCanonicalGenerationMeta || generationMeta !== undefined
        ? { generationMeta }
        : {}),
    } as RunProposal;
  });
}

function pendingInputKind(value: unknown): ValueInputKind | null {
  return value === 'credential' || value === 'otp' || value === 'text'
    ? value
    : null;
}

function pendingInputState(value: unknown): InputRequestState | null {
  return value === 'pending' ||
    value === 'answered' ||
    value === 'cancelled' ||
    value === 'expired'
    ? value
    : null;
}

function takeoverState(value: unknown): TakeoverState | null {
  return value === 'pending' ||
    value === 'submitted' ||
    value === 'answered' ||
    value === 'blocked' ||
    value === 'gave_up' ||
    value === 'expired' ||
    value === 'cancelled'
    ? value
    : null;
}

/**
 * Normalize a `{ width, height }` pair before it reaches the gesture mapper.
 *
 * The service rejects non-positive sizes on a raise, but GET responses are still untrusted at this
 * boundary (and older rows may predate the web fields). A null size makes the panel refuse to guess
 * coordinates instead of scaling a click by a malformed value.
 */
function takeoverSize(
  value: unknown,
): { width: number; height: number } | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const width = Number(row.width);
  const height = Number(row.height);
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { width: Math.round(width), height: Math.round(height) };
}

function takeoverVerify(value: unknown): TakeoverVerify | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row.kind !== 'string' || row.kind.length === 0) return null;
  for (const key of ['column', 'equals', 'labelContains', 'value']) {
    if (key in row && row[key] !== null && typeof row[key] !== 'string')
      return null;
  }
  return {
    kind: row.kind,
    ...(typeof row.column === 'string' ? { column: row.column } : {}),
    ...(typeof row.equals === 'string' ? { equals: row.equals } : {}),
    ...(typeof row.labelContains === 'string'
      ? { labelContains: row.labelContains }
      : {}),
    ...(typeof row.value === 'string' ? { value: row.value } : {}),
  };
}

function takeoverDevice(value: unknown): TakeoverDevice | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const hasModel = 'model' in row;
  const hasOsVersion = 'osVersion' in row;
  const hasScreen = 'screen' in row;
  if (!hasModel && !hasOsVersion && !hasScreen) return null;
  if (hasModel && typeof row.model !== 'string' && row.model !== null)
    return null;
  if (
    hasOsVersion &&
    typeof row.osVersion !== 'string' &&
    row.osVersion !== null
  )
    return null;
  const screen = takeoverSize(row.screen);
  if (hasScreen && !screen && row.screen !== null) return null;
  return {
    ...(hasModel ? { model: row.model as string | null } : {}),
    ...(hasOsVersion ? { osVersion: row.osVersion as string | null } : {}),
    ...(hasScreen ? { screen } : {}),
  };
}

function takeoverResults(value: unknown): TakeoverCommandResult[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): TakeoverCommandResult[] => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    if (
      typeof row.commandId !== 'string' ||
      typeof row.executed !== 'boolean' ||
      typeof row.changed !== 'boolean'
    ) {
      return [];
    }
    return [
      {
        commandId: row.commandId,
        executed: row.executed,
        changed: row.changed,
        error: typeof row.error === 'string' ? row.error : null,
        observed: typeof row.observed === 'string' ? row.observed : null,
      },
    ];
  });
}

/**
 * Converts one untrusted takeover row into the narrow DTO.
 *
 * `reason` and `expectation` ARE model-authored — a takeover exists because the agent is stuck, and only
 * the agent can say on what and what the human should achieve. They go through `normalizeInstruction`
 * for the same reason an `action` instruction does (Andrei's ruling, 2026-08-12): `safeShortText` would
 * fail closed on the digits a wheel value legitimately carries. The rest is structural, and the two
 * fields that decide what the browser can reach — `has_stream` and `stream_token` — are taken as their
 * own types, never composed from anything else.
 */
function normalizeTakeover(
  row: Record<string, unknown>,
  common: { request_id: string; case_id: string | null; key: string },
): DeviceTakeoverRequest[] {
  const state = takeoverState(row.state);
  const expiresAt = typeof row.expires_at === 'string' ? row.expires_at : '';
  if (!state || !expiresAt) return [];
  return [
    {
      ...common,
      kind: 'device_takeover',
      state,
      expires_at: expiresAt,
      descriptor: normalizeDescriptor(row.descriptor),
      target_key: typeof row.target_key === 'string' ? row.target_key : '',
      reason: safeOptionalInstruction(row.reason) ?? '',
      expectation: safeOptionalInstruction(row.expectation) ?? '',
      verify: takeoverVerify(row.verify),
      device: takeoverDevice(row.device),
      viewport: takeoverSize(row.viewport),
      frame: takeoverSize(row.frame),
      has_stream: row.has_stream === true,
      stream_token:
        typeof row.stream_token === 'string' ? row.stream_token : null,
      observed: safeOptionalInstruction(row.observed),
      results: takeoverResults(row.results),
      pending_commands:
        typeof row.pending_commands === 'number' &&
        Number.isFinite(row.pending_commands)
          ? Math.max(0, Math.floor(row.pending_commands))
          : 0,
      verified: typeof row.verified === 'boolean' ? row.verified : null,
      final_state: takeoverState(row.final_state),
    },
  ];
}

/** Converts untrusted JSON into the narrow viewer DTOs — value requests and device takeovers alike,
 *  discriminated on `kind`. Unknown/malformed rows are omitted, and no worker-authored `prompt` text
 *  survives: a value request carries only the server-composed `descriptor`. */
export function normalizePendingInputs(payload: unknown): HelpRequest[] {
  const rows = Array.isArray(payload) ? payload : [];
  return rows.flatMap((value): HelpRequest[] => {
    if (!value || typeof value !== 'object') return [];
    const row = value as Record<string, unknown>;
    const requestId = typeof row.request_id === 'string' ? row.request_id : '';
    const key = typeof row.key === 'string' ? row.key : '';
    if (!requestId || !key) return [];
    const common = {
      request_id: requestId,
      case_id: typeof row.case_id === 'string' ? row.case_id : null,
      key,
    };
    if (row.kind === 'device_takeover') return normalizeTakeover(row, common);
    const kind = pendingInputKind(row.kind);
    const state = pendingInputState(row.state);
    if (!kind || !state) return [];
    const request: ValueInputRequest = {
      ...common,
      kind,
      descriptor: normalizeDescriptor(row.descriptor),
      state,
    };
    if (typeof row.expires_at === 'string') {
      request.expires_at = row.expires_at;
    }
    return [request];
  });
}

/**
 * Every help request the run currently has — a value still awaiting an answer, or a device takeover a
 * human can still act on. Never carries a value: the service does not persist or return one (see
 * AnswerInputBody).
 */
export async function fetchPendingInputs(id: string): Promise<HelpRequest[]> {
  const body = await getJson<unknown>(
    `/runs/${encodeURIComponent(id)}/inputs`,
    'Failed to load pending inputs',
  );
  return normalizePendingInputs(body);
}

const AUTH_FLIGHT_STATES = new Set<AuthFlightState>([
  'pending',
  'applying',
  'answered',
  'expired',
  'reset',
  'account_mismatch',
]);

const AUTH_REASON_LABELS: Record<string, string> = {
  account_mismatch: 'A different account was detected',
  auth_input_expired: 'Authentication was not provided in time',
  authentication_required: 'Authentication required',
  credentials_required: 'Sign-in required',
  harness_incompatibility:
    'This test cannot continue in the current browser environment',
  login_required: 'Sign-in required',
  mfa_required: 'Microsoft verification required',
  otp_required: 'One-time code required',
  session_expired: 'The saved sign-in session expired',
};

/** AuthFlight endpoints live here so the UI never hand-builds route variants. */
export const authFlightPaths = {
  list: (runId: string) => `/runs/${encodeURIComponent(runId)}/auth-flights`,
  answer: (runId: string, flightId: string) =>
    `/runs/${encodeURIComponent(runId)}/auth-flights/${encodeURIComponent(flightId)}/answer`,
  reset: (runId: string) =>
    `/runs/${encodeURIComponent(runId)}/auth-flights/reset`,
};

function safeShortText(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const cleaned = value
    .normalize('NFKC')
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  if (!cleaned) return fallback;
  // A safe label is descriptive prose, never a value-bearing assignment, account identifier,
  // URL, OTP, JSON fragment, or HTML fragment. Fail closed to a client-owned neutral label.
  if (
    /(?:password|passcode|otp|mfa|token|cookie|storage[ _-]?state|authorization|secret)\s*[:=]/i.test(
      cleaned,
    ) ||
    /\b\d{4,8}\b/.test(cleaned) ||
    /(?:https?:\/\/|@|[<>{}\[\]])/.test(cleaned)
  ) {
    return fallback;
  }
  return cleaned;
}

/** Strips control characters, collapses whitespace and caps length — mirrors the service's own
 *  `normalizeInstruction` (`request-descriptor.ts`) exactly, so an `action` instruction reaches the
 *  DOM with its digits and punctuation intact (React's escaping is the remaining defense). */
function normalizeInstruction(instruction: string): string {
  const withoutControlBytes = Array.from(instruction)
    .map((character) => {
      const code = character.charCodeAt(0);
      return code <= 0x1f || code === 0x7f ? ' ' : character;
    })
    .join('');
  return withoutControlBytes.replace(/\s+/g, ' ').trim().slice(0, 500);
}

/** Passes a model-authored `instruction` through `normalizeInstruction` only — never through the
 *  fail-closed `safeShortText`, which would blank the digit an instruction legitimately carries. */
function safeOptionalInstruction(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return normalizeInstruction(value) || null;
}

/** A value-bearing assignment of something secret — `password=…`, `token: …`. The one shape that must
 *  never reach the DOM through a descriptor part, whatever composed it. */
const SECRET_ASSIGNMENT =
  /(?:password|passcode|otp|mfa|token|cookie|storage[ _-]?state|authorization|secret)\s*[:=]/i;

/** Normalizes a descriptor part the server composed from data our own code owns: the environment name
 *  a human typed into project settings, the case reference our code derived from a case id, or the step
 *  text read out of our own `test_results` row.
 *
 *  Deliberately NOT `safeShortText`. That guard fails closed on any four-to-eight digit run, any `@`
 *  and any URL, so applying it here silently blanked an environment named "Release 2026" and another
 *  called "qa@example.com" — dropping the part the operator most needs and breaking the rule that Teams
 *  and this panel show the same description. It stays in force for every worker-authored field on the
 *  flight contract, where model text really can arrive.
 *
 *  What survives from that guard is the one rule that still earns its place: a secret-assignment shape
 *  is dropped rather than rendered. Provenance here is strong but not airtight — an environment name can
 *  reach a run through the launch DTO — so a part that reads like `password=hunter2` is refused even
 *  though our own code composed the field around it. */
function safeOptionalOwnText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = normalizeInstruction(value);
  if (!normalized || SECRET_ASSIGNMENT.test(normalized)) return null;
  return normalized;
}

/** Converts an untrusted `descriptor` value into the narrow `RequestDescriptor` DTO. A row with no
 *  `field` (a pre-T3 row, or a malformed one) yields `undefined`. Each part the server composed from
 *  its own data goes through `safeOptionalOwnText`, which normalizes and refuses a secret-assignment
 *  shape but does NOT fail closed on digits, `@` or URLs — an earlier version ran these through
 *  `safeShortText` and silently dropped an environment named `Release 2026`, so the panel and the
 *  service's own Teams composer disagreed on the same request. `safeShortText` itself is unchanged
 *  and still guards every worker-authored field on the flight contract.
 *  `instruction` is normalized rather than guarded (Andrei's ruling, 2026-08-12), and is
 *  accepted only for `field='action'` — a value-field row carries no instruction even if one
 *  somehow arrives on the wire, so a server-side regression can't smuggle model text past the
 *  barrier through a field that was never supposed to carry any. */
function normalizeDescriptor(value: unknown): RequestDescriptor | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  const field = typeof row.field === 'string' ? row.field : '';
  if (!field) return undefined;
  return {
    field,
    environment_name: safeOptionalOwnText(row.environment_name),
    case_ref: safeOptionalOwnText(row.case_ref),
    step_text: safeOptionalOwnText(row.step_text),
    instruction:
      field === 'action' ? safeOptionalInstruction(row.instruction) : null,
  };
}

function safeReasonCode(value: unknown): string {
  return typeof value === 'string' && /^[a-z0-9_.-]{1,80}$/.test(value)
    ? value
    : 'authentication_required';
}

function safeEnvironmentAlias(value: unknown): string | null {
  const alias = safeShortText(value, '');
  if (
    !alias ||
    /\b(?:password|passcode|otp|mfa|token|cookie|storage|authorization|secret|enter|provide)\b/i.test(
      alias,
    )
  ) {
    return null;
  }
  return alias;
}

function safeEnvironmentResetKey(value: unknown): string | null {
  return typeof value === 'string' && /^aer_[a-f0-9]{32}$/.test(value)
    ? value
    : null;
}

function authFlightRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const rows = (payload as { flights?: unknown }).flights;
  return Array.isArray(rows) ? rows : [];
}

function authFlightState(value: unknown): AuthFlightState | null {
  const state = value === 'requested' ? 'pending' : value;
  if (
    typeof state === 'string' &&
    AUTH_FLIGHT_STATES.has(state as AuthFlightState)
  ) {
    return state as AuthFlightState;
  }
  return null;
}

/** Converts untrusted JSON into the narrow viewer DTO. Unknown/malformed rows are omitted, and no
 * extra properties survive — notably worker prompt/key, case payloads, or secret-bearing fields. */
export function normalizeAuthFlights(payload: unknown): AuthFlight[] {
  return authFlightRows(payload).flatMap((value): AuthFlight[] => {
    if (!value || typeof value !== 'object') return [];
    const row = value as Record<string, unknown>;
    const flightId = typeof row.flight_id === 'string' ? row.flight_id : '';
    const runId = typeof row.run_id === 'string' ? row.run_id : '';
    const state = authFlightState(row.state);
    if (!flightId || !runId || !state) return [];
    const environmentId =
      typeof row.environment_id === 'string' ? row.environment_id : null;
    const environmentAlias = safeEnvironmentAlias(row.environment_alias);
    const environmentResetKey = safeEnvironmentResetKey(
      row.environment_reset_key,
    );
    const reasonCode = safeReasonCode(row.reason_code);
    const fallbackLabel =
      AUTH_REASON_LABELS[reasonCode] ?? 'Authentication required';
    const waitingCount =
      typeof row.waiting_case_count === 'number' &&
      Number.isFinite(row.waiting_case_count)
        ? Math.max(0, Math.floor(row.waiting_case_count))
        : 0;
    const rawExpiry =
      typeof row.expires_at === 'string' ? row.expires_at : null;
    const expiresAt =
      rawExpiry && Number.isFinite(Date.parse(rawExpiry)) ? rawExpiry : null;
    const mismatch =
      row.account_mismatch === true || state === 'account_mismatch';
    return [
      {
        flight_id: flightId,
        run_id: runId,
        environment_id: environmentId,
        environment_alias: environmentAlias,
        environment_reset_key: environmentResetKey,
        environment_name:
          safeOptionalOwnText(row.environment_name) ??
          environmentAlias ??
          (environmentId ? 'Selected environment' : 'No environment'),
        kind: 'auth',
        reason_code: reasonCode,
        state,
        waiting_case_count: waitingCount,
        expires_at: expiresAt,
        account_mismatch: mismatch,
        safe_label: safeShortText(row.safe_label, fallbackLabel),
        descriptor: normalizeDescriptor(row.descriptor),
        parent_flight_id:
          typeof row.parent_flight_id === 'string'
            ? row.parent_flight_id
            : null,
        is_exception: row.is_exception === true || mismatch,
      },
    ];
  });
}

/** Environment-scoped authentication waits for a run. */
export async function fetchAuthFlights(id: string): Promise<AuthFlight[]> {
  try {
    const body = await getJson<unknown>(
      authFlightPaths.list(id),
      'Failed to load authentication requests',
    );
    return normalizeAuthFlights(body);
  } catch (error) {
    // Deploy-safe compatibility: an older quality service has no AuthFlight route, but its legacy
    // non-auth input panel must keep working without showing an error on every run detail page.
    if (error instanceof QualityApiError && error.status === 404) return [];
    throw error;
  }
}

/** Answers one flight. The response is state-only and never echoes the submitted secret. */
export async function answerAuthFlight(
  runId: string,
  flightId: string,
  body: AnswerAuthFlightBody,
): Promise<{ state: AuthFlightState }> {
  const res = await apiFetch(
    `${API_BASE}${authFlightPaths.answer(runId, flightId)}`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to apply authentication'),
    );
  }
  const result = (await res.json()) as { state?: unknown };
  return { state: authFlightState(result.state) ?? 'applying' };
}

/** Clears saved auth state only for the selected environment. */
export async function resetAuthFlightEnvironment(
  runId: string,
  body: ResetAuthFlightBody,
): Promise<{
  state: AuthFlightState;
  cleanup_status: 'complete' | 'pending';
}> {
  const res = await apiFetch(`${API_BASE}${authFlightPaths.reset(runId)}`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to reset authentication'),
    );
  }
  const result: unknown = await res.json().catch(() => null);
  return {
    state: 'reset',
    cleanup_status:
      result !== null &&
      typeof result === 'object' &&
      'cleanup_status' in result &&
      result.cleanup_status === 'complete'
        ? 'complete'
        : 'pending',
  };
}

/**
 * The device takeover a human may act on for this run, or `{ state: 'idle' }`.
 *
 * A takeover is the HITL channel's other shape: instead of typing a value, the human drives the device
 * itself while the agent waits. The response never carries the session's attach URL or the farm host —
 * the video comes through the service's own proxied stream route.
 *
 * Fields mirror DeviceTakeoverRequest (the same takeover as it appears in the pending-inputs list) minus
 * `kind`/`key`/`prompt` — this route is already scoped to "the" takeover for one run, so it never needs
 * to say which kind of help request it is.
 */
export interface ActiveTakeover {
  state: 'idle' | TakeoverState;
  request_id?: string;
  case_id?: string | null;
  target_key?: string;
  reason?: string;
  expectation?: string;
  expires_at?: string;
  verify?: TakeoverVerify | null;
  /** `screen` is in device POINTS — the panel scales a click by it. */
  device?: TakeoverDevice | null;
  /** Web: the page's CSS-pixel size and the size of the streamed frames. */
  viewport?: { width: number; height: number } | null;
  frame?: { width: number; height: number } | null;
  has_stream?: boolean;
  /** Capability token for the live view. The SPA authenticates with a Bearer JWT, and an <img> cannot
   *  send a header — so the stream URL carries this instead. Scoped to this takeover and dies with it. */
  stream_token?: string | null;
  observed?: string | null;
  results?: TakeoverCommandResult[];
  pending_commands?: number;
  verified?: boolean | null;
  /** Null unless the takeover actually settled — see DeviceTakeoverRequest's doc comment for what
   *  distinguishes that from `state` falling back to 'expired' on its own. */
  final_state?: TakeoverState | null;
}

function normalizeActiveTakeover(value: unknown): ActiveTakeover {
  if (!value || typeof value !== 'object') return { state: 'idle' };
  const row = value as Record<string, unknown>;
  const state = row.state === 'idle' ? 'idle' : takeoverState(row.state);
  if (!state) return { state: 'idle' };
  const normalized: ActiveTakeover = { state };
  if (typeof row.request_id === 'string')
    normalized.request_id = row.request_id;
  if (typeof row.case_id === 'string' || row.case_id === null)
    normalized.case_id = row.case_id;
  if (typeof row.target_key === 'string')
    normalized.target_key = row.target_key;
  if (typeof row.reason === 'string') normalized.reason = row.reason;
  if (typeof row.expectation === 'string')
    normalized.expectation = row.expectation;
  if (typeof row.expires_at === 'string')
    normalized.expires_at = row.expires_at;

  const verify = takeoverVerify(row.verify);
  if (verify || row.verify === null) normalized.verify = verify;
  const device = takeoverDevice(row.device);
  if (device || row.device === null) normalized.device = device;
  const viewport = takeoverSize(row.viewport);
  if (viewport || row.viewport === null) normalized.viewport = viewport;
  const frame = takeoverSize(row.frame);
  if (frame || row.frame === null) normalized.frame = frame;

  if (typeof row.has_stream === 'boolean')
    normalized.has_stream = row.has_stream;
  if (typeof row.stream_token === 'string' || row.stream_token === null)
    normalized.stream_token = row.stream_token;
  if (typeof row.observed === 'string' || row.observed === null)
    normalized.observed = row.observed;
  if (Array.isArray(row.results)) {
    const results = takeoverResults(row.results);
    if (results.length === row.results.length) normalized.results = results;
  }
  if (
    typeof row.pending_commands === 'number' &&
    Number.isFinite(row.pending_commands) &&
    row.pending_commands >= 0
  ) {
    normalized.pending_commands = Math.floor(row.pending_commands);
  }
  if (typeof row.verified === 'boolean' || row.verified === null)
    normalized.verified = row.verified;
  const finalState = takeoverState(row.final_state);
  if (finalState || row.final_state === null)
    normalized.final_state = finalState;
  return normalized;
}

export async function fetchActiveTakeover(id: string): Promise<ActiveTakeover> {
  const takeover = await getJson<unknown>(
    `/runs/${encodeURIComponent(id)}/takeover`,
    'Failed to load the device takeover',
  );
  return normalizeActiveTakeover(takeover);
}

/**
 * The proxied live view, for an <img> src.
 *
 * The token is the credential: an <img> cannot carry the Bearer header apiFetch injects, so a stream route
 * behind @RequireAuth() is unreachable from the panel (measured — the frame rendered as broken alt text).
 * The token is minted per takeover server-side and only handed to a caller that already authenticated.
 *
 * No run id in the path, on purpose: the gateway can only allow a public route by path prefix, and it 401s
 * this request before the service sees the token unless that prefix exists. `/v1/quality/takeover-stream`
 * is a prefix narrow enough to make public; `/v1/quality/runs` is not.
 */
export function takeoverStreamUrl(
  streamToken: string,
  options: { version?: 'v2' } = {},
): string {
  const params = new URLSearchParams({ token: streamToken });
  if (options.version === 'v2') params.set('v', '2');
  return `${API_BASE}/takeover-stream?${params.toString()}`;
}

export type TakeoverStreamObservation =
  | {
      event: 'stream_open' | 'stream_reconnect' | 'stream_eof' | 'stream_error';
      request_id: string;
      occurred_at: string;
      reconnect_count?: number;
    }
  | {
      event: 'alert';
      request_id: string;
      occurred_at: string;
      alert_played: boolean;
      alert_blocked: boolean;
    }
  | {
      event: 'frame_received' | 'frame_rendered' | 'heartbeat';
      request_id: string;
      occurred_at: string;
      seq?: number;
      captured_at?: string | null;
      published_at?: string | null;
      received_at?: string;
      rendered_at?: string;
    };

export async function observeTakeoverStream(
  runId: string,
  requestId: string,
  observation: TakeoverStreamObservation,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/takeover/${encodeURIComponent(requestId)}/observations`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(observation),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to record stream observation'),
    );
  }
}

export type TakeoverCommandBody =
  | { kind: 'tap'; x: number; y: number }
  | {
      kind: 'drag';
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      steps?: number;
    }
  | { kind: 'type'; text: string };

/**
 * Queue one gesture. Coordinates are in the far side's own space — device points for a phone, CSS pixels
 * for a page — and `toDevicePoints` is what converts a click in the displayed frame into it.
 *
 * `type` is web-only: a device has an on-screen keyboard the human taps, so a mobile takeover types by
 * tapping it and never sends this.
 */
export async function queueTakeoverCommand(
  runId: string,
  requestId: string,
  body: TakeoverCommandBody,
): Promise<{ commandId: string; seq: number }> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/takeover/${encodeURIComponent(requestId)}/command`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to send the gesture'),
    );
  }
  return (await res.json()) as { commandId: string; seq: number };
}

/**
 * End the human's turn. Submit does NOT complete the takeover — the worker re-reads the device and
 * verifies, so a human cannot mark a case green from the browser.
 */
export async function settleTakeover(
  runId: string,
  requestId: string,
  kind: 'submit' | 'give_up',
): Promise<{ state: string }> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/takeover/${encodeURIComponent(requestId)}/settle`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ kind }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to end the takeover'),
    );
  }
  return (await res.json()) as { state: string };
}

/** Answer a pending request_user_input() call — the value is never echoed back. */
export async function answerInput(
  runId: string,
  requestId: string,
  body: AnswerInputBody,
): Promise<{ state: string }> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/inputs/${encodeURIComponent(requestId)}`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to submit the value'),
    );
  }
  return (await res.json()) as { state: string };
}

/** Approve the current proposed version → create the selected cases in TestRail and finalize. */
export async function approveRun(
  id: string,
  body: ApproveBody = {},
): Promise<Run> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/approve`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to approve'),
    );
  }
  return (await res.json()) as Run;
}

/** Publish a selected, non-overlapping subset of one durable generation proposal. */
export async function createGeneratedCasePublicationBatch(
  runId: string,
  proposalId: string,
  body: CreateGeneratedCasePublicationBatchBody,
): Promise<CreateGeneratedCasePublicationBatchResponse> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/proposals/${encodeURIComponent(proposalId)}/publication-batches`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok) {
    throw await responseError(res, 'Failed to create test cases');
  }
  return (await res.json()) as CreateGeneratedCasePublicationBatchResponse;
}

/** Retry only the retained story relations for an already-created publication. The service never
 * recreates the external case on this route; its durable publication identity is the source of
 * truth for the retry. */
export async function retryGeneratedCasePublication(
  runId: string,
  proposalId: string,
  publicationId: string,
): Promise<GeneratedCasePublication> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/proposals/${encodeURIComponent(proposalId)}/publications/${encodeURIComponent(publicationId)}/retry`,
    { method: 'POST', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to retry story links'),
    );
  }
  return (await res.json()) as GeneratedCasePublication;
}

/** Request changes on the current proposed version → enqueue a refine round with the feedback. */
export async function requestChanges(
  id: string,
  body: RequestChangesBody,
): Promise<Run> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/request-changes`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to request changes'),
    );
  }
  return (await res.json()) as Run;
}

export async function cancelCase(runId: string, caseId: string): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/cases/${encodeURIComponent(caseId)}/cancel`,
    {
      method: 'POST',
      headers: await headers(),
    },
  );
  if (!res.ok)
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to cancel case'),
    );
}

export function useCancelCase(
  runId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => cancelCase(runId, caseId),
    onSettled: (_data, error) => {
      if (
        error &&
        (!(error instanceof QualityApiError) || error.status !== 409)
      )
        return;
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
      void qc.invalidateQueries({
        queryKey: qualityKeys.reportBundleBase(runId),
      });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export async function cancelRun(id: string): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/cancel`,
    {
      method: 'POST',
      headers: await headers(),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to cancel run'),
    );
  }
}

/** Remove a finished run from Quality. In-progress runs must be cancelled first. */
export async function deleteRun(id: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}/runs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await headers(),
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to delete run'),
    );
  }
}

/** Archive or un-archive a run: hides it from (or restores it to) the default runs board without
 *  ever deleting it. Owner or admin, like {@link deleteRun}. */
export async function setRunArchived(
  id: string,
  archived: boolean,
): Promise<Run> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/archive`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ archived }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(
        res,
        archived ? 'Failed to archive run' : 'Failed to un-archive run',
      ),
    );
  }
  return (await res.json()) as Run;
}

/** Restart a run's failed cases. `caseIds` (MEDA-352) scopes it to specific cases (e.g. the ones just
 *  refined); omitted ⇒ every failed case, as before. */
export interface RelaunchRunBody {
  /** Omission inherits the source run; null explicitly restores automatic platform selection. */
  targetPlatforms?: Platform[] | null;
  parallelism?: number;
  model?: string;
  capability?: Capability;
  takeoverEnabled?: boolean;
  environmentMode?: EnvironmentMode;
  environmentName?: string;
  humanInteractionEnabled?: boolean;
  webNetworkCaptureMode?: WebNetworkCaptureOverride;
  caseIds?: string[];
  includeOutcomes?: Extract<CaseOutcome, 'defect_found' | 'case_unclear'>[];
}

export interface RerunCurrentBody {
  failedOnly?: boolean;
  driftConfirmationToken?: string;
  launchRequestKey?: string;
}

function serializeRerunCurrentBody(
  body: RerunCurrentBody,
): Record<string, unknown> {
  return {
    ...(body.failedOnly !== undefined ? { failed_only: body.failedOnly } : {}),
    ...(body.driftConfirmationToken !== undefined
      ? { drift_confirmation_token: body.driftConfirmationToken }
      : {}),
    ...(body.launchRequestKey !== undefined
      ? { launch_request_key: body.launchRequestKey }
      : {}),
  };
}

export async function restartFailed(
  id: string,
  caseIds?: string[],
  settings?: RelaunchRunBody,
): Promise<Run> {
  await assertCurrentRunContract('quality.restart_failed', {
    ...settings,
    ...(caseIds?.length ? { caseIds } : {}),
  });
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/restart-failed`,
    {
      method: 'POST',
      headers: await headers(),
      ...(settings || (caseIds && caseIds.length > 0)
        ? {
            body: JSON.stringify({
              ...settings,
              ...(settings?.caseIds?.length
                ? { caseIds: settings.caseIds }
                : caseIds?.length
                  ? { caseIds }
                  : {}),
            }),
          }
        : {}),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to restart run'),
    );
  }
  return (await res.json()) as Run;
}

// ── Refine flow (MEDA-352) ───────────────────────────────────────────────────

/** Poorly-described-case findings for a run (drives the refine banner + the affected-case list). */
export async function fetchPoorlyDescribedFindings(
  id: string,
): Promise<ReplayFinding[]> {
  const data = await getJson<{ findings: ReplayFinding[] }>(
    `/findings?run_id=${encodeURIComponent(id)}&type=poorly_described_case`,
    'Failed to load findings',
  );
  return data.findings;
}

/** The run's refine revisions (rewrite lifecycle for each case the user asked to refine). */
export async function fetchCaseRevisions(id: string): Promise<CaseRevision[]> {
  const data = await getJson<{ case_revisions: CaseRevision[] }>(
    `/runs/${encodeURIComponent(id)}/case-revisions`,
    'Failed to load case revisions',
  );
  return data.case_revisions;
}

/** Ask the worker to rewrite the given flagged cases; returns the pending revisions. */
export async function refineCases(
  id: string,
  caseIds: string[],
): Promise<CaseRevision[]> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/refine-cases`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ caseIds }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to refine cases'),
    );
  }
  const data = (await res.json()) as { case_revisions: CaseRevision[] };
  return data.case_revisions;
}

/** Apply a proposed rewrite (writes back to TMS best-effort + updates the run spec for re-run). */
export async function applyCaseRevision(
  id: string,
  revisionId: string,
  editedSpec?: CaseSpec,
): Promise<ApplyRevisionResult> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/case-revisions/${encodeURIComponent(revisionId)}/apply`,
    {
      method: 'POST',
      headers: await headers(),
      // A user-edited rewrite is sent as `proposed_spec` (snake_case wire shape); absent ⇒ the server
      // applies the stored proposal unchanged.
      ...(editedSpec
        ? { body: JSON.stringify({ proposed_spec: editedSpec }) }
        : {}),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to apply rewrite'),
    );
  }
  return (await res.json()) as ApplyRevisionResult;
}

/** Dismiss a proposed rewrite (the finding drops back to reviewed). */
export async function dismissCaseRevision(
  id: string,
  revisionId: string,
): Promise<CaseRevision> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/case-revisions/${encodeURIComponent(revisionId)}/dismiss`,
    { method: 'POST', headers: await headers() },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to dismiss rewrite'),
    );
  }
  return (await res.json()) as CaseRevision;
}

// ── Regression jobs ──────────────────────────────────────────────────────────
// Saved run-templates (any CreateRunDto subset) with an optional cron. The service owns the template;
// the schedule lives in cti-scheduler. All CRUD goes through /v1/quality/regression — the SPA never
// calls the scheduler directly. Requests are camelCase; rows come back snake_case (mapped below).

/** A regression no longer carries a model, so a legacy stored one is dropped here and an edit never
 *  sends it back. Every other key passes through untouched. */
function withoutLegacyModel(
  runConfig: RegressionRunConfig,
): RegressionRunConfig {
  if (!runConfig || !('model' in runConfig)) return runConfig;
  const { model: _legacyModel, ...rest } = runConfig as RegressionRunConfig & {
    model?: unknown;
  };
  return rest;
}

/** Map a snake_case job row to the camelCase shape the components consume. `run_config` is opaque
 *  jsonb whose inner keys are already camelCase, so it is passed through as is, minus a legacy model. */
function toRegressionJob(row: RegressionJobRow): RegressionJob {
  return {
    requestKey: row.request_key,
    provisioningState: row.provisioning_state,
    id: row.id,
    name: row.name,
    projectId: row.project_id,
    runConfig: withoutLegacyModel(row.run_config),
    cron: row.cron,
    tz: row.tz,
    enabled: row.enabled,
    createdBy: row.created_by,
    schedulerAutomationId: row.scheduler_automation_id,
    schedulingStatus: row.scheduling_status,
    lastRunId: row.last_run_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    notifyCreatorOnFail: row.notify_creator_on_fail,
    callbackEnabled: row.callback_enabled,
    callbackUrlMasked: row.callback_url_masked,
    callbackDisabled: row.callback_disabled,
    callbackFailureCount: row.callback_failure_count,
    callbackLastError: row.callback_last_error,
  };
}

export async function fetchRegressionJobs(
  projectId: string,
): Promise<RegressionJob[]> {
  const rows = await getJson<RegressionJobRow[]>(
    `/regression?projectId=${encodeURIComponent(projectId)}`,
    'Failed to load regression jobs',
  );
  return rows.map(toRegressionJob);
}

export async function fetchRegressionJob(id: string): Promise<RegressionJob> {
  const row = await getJson<RegressionJobRow>(
    `/regression/${encodeURIComponent(id)}`,
    'Failed to load regression job',
  );
  return toRegressionJob(row);
}

export async function createRegressionJob(
  body: CreateRegressionJobBody,
): Promise<RegressionJob> {
  // Negotiate only what this body actually sends: the interface creates a job without an
  // idempotency key, so requiring one would refuse every job it authors.
  await assertCurrentQualityFields('quality.regression_create', [
    'run_config',
    ...(body.requestKey ? ['request_key'] : []),
  ]);
  const res = await apiFetch(`${API_BASE}/regression`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to create regression job'),
    );
  }
  return toRegressionJob((await res.json()) as RegressionJobRow);
}

export async function updateRegressionJob(
  id: string,
  body: UpdateRegressionJobBody,
): Promise<RegressionJob> {
  if (body.runConfig) {
    await assertCurrentQualityFields('quality.regression_update', [
      'run_config',
    ]);
  }
  const res = await apiFetch(
    `${API_BASE}/regression/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to update regression job'),
    );
  }
  return toRegressionJob((await res.json()) as RegressionJobRow);
}

export async function deleteRegressionJob(id: string): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/regression/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: await headers(),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to delete regression job'),
    );
  }
}

/** Re-provision the scheduler automation for a job whose `schedulingStatus` is `pending`. */
export async function retryRegressionSchedule(
  id: string,
): Promise<RegressionJob> {
  const res = await apiFetch(
    `${API_BASE}/regression/${encodeURIComponent(id)}/retry-schedule`,
    {
      method: 'POST',
      headers: await headers(),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to retry scheduling'),
    );
  }
  return toRegressionJob((await res.json()) as RegressionJobRow);
}

/** Launch a job's run-template immediately (direct quality launch, any project runner). The server
 *  returns snake_case `{ run_id }`; mapped to `{ runId }` so the caller can route to the run. */
export async function runRegressionJobNow(
  id: string,
): Promise<{ runId: string }> {
  const res = await apiFetch(
    `${API_BASE}/regression/${encodeURIComponent(id)}/run-now`,
    {
      method: 'POST',
      headers: await headers(),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to launch run'),
    );
  }
  const json = (await res.json()) as { run_id: string };
  return { runId: json.run_id };
}

/** A job's run history (newest first). Snake_case run summaries, consumed as-is. */
export async function fetchRegressionJobRuns(
  id: string,
): Promise<RegressionRunSummary[]> {
  return getJson<RegressionRunSummary[]>(
    `/regression/${encodeURIComponent(id)}/runs`,
    'Failed to load run history',
  );
}

// ── Agent-name suggestion ────────────────────────────────────────────────────
// Best-effort name generation for a regression job, served by the automations BFF at
// /v1/automations/generate-name (NOT under /v1/quality). May be slow; callers handle failure by
// leaving the manual name intact.

/** POST /v1/automations/generate-name — turn a prompt describing the job into a suggested name. */
export async function generateAgentName(prompt: string): Promise<string> {
  const res = await apiFetch('/v1/automations/generate-name', {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to generate a name'),
    );
  }
  const json = (await res.json()) as { name: string };
  return json.name;
}

/** Re-run a whole run again — every case it ran, not just the failed ones. Creates a fresh run with
 *  the same cases/suite/project and returns it (navigate to the new run's id). */
export async function rerunRun(
  id: string,
  settings?: RelaunchRunBody,
): Promise<Run> {
  await assertCurrentRunContract('quality.rerun_all', settings ?? {});
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/rerun`,
    {
      method: 'POST',
      headers: await headers(),
      ...(settings ? { body: JSON.stringify(settings) } : {}),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to re-run'),
    );
  }
  return (await res.json()) as Run;
}

/** Re-run the original selected source cases against current provider content and saved adaptations. */
export async function rerunCurrentRun(
  id: string,
  body: RerunCurrentBody = {},
): Promise<Run> {
  await assertCurrentRunContract('quality.rerun_current', body);
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(id)}/rerun-current`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(serializeRerunCurrentBody(body)),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to re-run the current source'),
    );
  }
  return (await res.json()) as Run;
}

// ── Native JSON report ─────────────────────────────────────────────────────
// The worker emits the combined report as JSON under runs/{id}/report/*.json; the SPA renders it
// natively in the Cases + Video tabs. Individual legacy callers treat the files as best-effort;
// bundle reads are strict because an authorization, transport, or parse failure must not look like
// an absent optional document and accidentally make a partial canonical bundle exportable.

function isReportRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isReportRecordArray(
  value: unknown,
): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(isReportRecord);
}

function hasOptionalRecordArray(
  record: Record<string, unknown>,
  key: string,
): boolean {
  return record[key] === undefined || isReportRecordArray(record[key]);
}

function optionalReportText(value: unknown): boolean {
  return value == null || typeof value === 'string';
}

function hasSafeReportText(
  record: Record<string, unknown>,
  keys: string[],
): boolean {
  return keys.every((key) => optionalReportText(record[key]));
}

function hasSafeReportEvents(value: unknown): boolean {
  return (
    value === undefined ||
    (isReportRecordArray(value) &&
      value.every(
        (event) =>
          typeof event.kind === 'string' &&
          hasSafeReportText(event, [
            'name',
            'input',
            'content',
            'id',
            'tool_use_id',
          ]) &&
          (event.cost == null || typeof event.cost === 'number'),
      ))
  );
}

function hasReportOutcomeResults(
  value: unknown,
  schemaVersion: 4 | 5,
): boolean {
  const required = CASE_OUTCOMES.filter((outcome) => outcome !== 'unresolved');
  return (
    isReportRecord(value) &&
    Object.keys(value).every((key) =>
      CASE_OUTCOMES.includes(key as CaseOutcome),
    ) &&
    Object.keys(value).length ===
      required.length +
        (Object.prototype.hasOwnProperty.call(value, 'unresolved') ? 1 : 0) &&
    (schemaVersion === 5 ||
      !Object.prototype.hasOwnProperty.call(value, 'unresolved')) &&
    [
      ...required,
      ...(Object.hasOwn(value, 'unresolved') ? ['unresolved'] : []),
    ].every(
      (outcome) =>
        typeof value[outcome] === 'number' &&
        Number.isInteger(value[outcome]) &&
        Number(value[outcome]) >= 0,
    )
  );
}

function hasSafeStepArrays(record: Record<string, unknown>): boolean {
  if (!hasOptionalRecordArray(record, 'steps')) return false;
  const steps = record.steps as Record<string, unknown>[] | undefined;
  return (steps ?? []).every(
    (step) =>
      hasSafeReportText(step, [
        'name',
        'status',
        'observation',
        'expected_text',
        'actual_text',
        'component',
      ]) &&
      hasOptionalRecordArray(step, 'attachments') &&
      ((step.attachments ?? []) as Record<string, unknown>[]).every(
        (attachment) => hasSafeReportText(attachment, ['type', 'name', 'key']),
      ) &&
      hasOptionalRecordArray(step, 'app_exchanges'),
  );
}

function hasSafeCaseArrays(record: Record<string, unknown>): boolean {
  if (
    !hasSafeReportText(record, [
      'id',
      'case_id',
      'title',
      'status',
      'outcome',
    ]) ||
    !(
      record.error == null ||
      typeof record.error === 'string' ||
      (isReportRecord(record.error) && optionalReportText(record.error.message))
    ) ||
    !hasSafeStepArrays(record) ||
    !hasOptionalRecordArray(record, 'attempts') ||
    !hasOptionalRecordArray(record, 'videos')
  ) {
    return false;
  }
  const attempts = record.attempts as Record<string, unknown>[] | undefined;
  return (attempts ?? []).every(
    (attempt) =>
      hasSafeReportText(attempt, ['status', 'outcome']) &&
      hasSafeStepArrays(attempt) &&
      hasOptionalRecordArray(attempt, 'videos'),
  );
}

function hasStrictReportShape(
  file: string,
  value: Record<string, unknown>,
): boolean {
  // Strict bundle reads protect both supported protocols; individual legacy getters remain best effort.
  if (
    value.outcome_protocol_version !== 1 &&
    value.outcome_protocol_version !== null
  )
    return false;
  if (
    value.schema_version !== undefined &&
    (!Number.isSafeInteger(value.schema_version) ||
      Number(value.schema_version) < 1 ||
      Number(value.schema_version) > 5)
  )
    return false;
  const schemaVersion = value.schema_version === 5 ? 5 : 4;
  if (schemaVersion === 4 && value.history !== undefined) return false;
  if (file === 'cases.json') {
    const cases = value.cases;
    return (
      isReportRecordArray(cases) &&
      cases.every(
        (item) => typeof item.id === 'string' && hasSafeCaseArrays(item),
      )
    );
  }
  if (file === 'summary.json') {
    if (!hasOptionalRecordArray(value, 'sessions')) return false;
    const sessions = value.sessions as Record<string, unknown>[] | undefined;
    return (sessions ?? []).every(
      (session) =>
        typeof session.session_id === 'string' &&
        Array.isArray(session.case_ids) &&
        session.case_ids.every((id) => typeof id === 'string') &&
        (session.video == null ||
          (isReportRecord(session.video) &&
            hasSafeReportText(session.video, ['kind', 'value']))),
    );
  }
  if (file === 'error-digest.json') {
    if (
      value.outcome_protocol_version === 1 &&
      !hasReportOutcomeResults(value.results, schemaVersion)
    )
      return false;
    if (
      value.outcome_protocol_version === null &&
      (!isReportRecordArray(value.failed) || !isReportRecordArray(value.passed))
    )
      return false;
    return ['failed', ...CASE_OUTCOMES].every((bucket) => {
      if (!hasOptionalRecordArray(value, bucket)) return false;
      const entries = value[bucket] as Record<string, unknown>[] | undefined;
      return (entries ?? []).every(
        (entry) =>
          typeof entry.id === 'string' &&
          hasSafeReportText(entry, ['title', 'status', 'error']) &&
          hasSafeReportEvents(entry.last_events),
      );
    });
  }
  return true;
}

async function getReportJson<T>(
  id: string,
  file: string,
  options: { strict?: boolean } = {},
): Promise<T | null> {
  try {
    const res = await apiFetch(
      `${API_BASE}/runs/${encodeURIComponent(id)}/report/${file}`,
      {
        headers: await headers(),
      },
    );
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new QualityApiError(
        res.status,
        await errorMessage(res, `Failed to load ${file}`),
      );
    }
    const value: unknown = await res.json();
    if (options.strict) {
      if (!isReportRecord(value) || !hasStrictReportShape(file, value))
        throw new TypeError(`Invalid ${file} report response`);
    }
    return value as T;
  } catch (error) {
    if (options.strict) throw error;
    return null;
  }
}

export function getReportSummary(id: string): Promise<ReportSummary | null> {
  return getReportJson<ReportSummary>(id, 'summary.json');
}

export function getReportCases(id: string): Promise<ReportCases | null> {
  return getReportJson<ReportCases>(id, 'cases.json');
}

export function getReportErrorDigest(
  id: string,
): Promise<ReportErrorDigest | null> {
  return getReportJson<ReportErrorDigest>(id, 'error-digest.json');
}

const REPORT_BUNDLE_REFETCHES = 2;

function reportDocumentRevision(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const revision = (value as { report_revision?: unknown }).report_revision;
  return Number.isInteger(revision) && Number(revision) >= 0
    ? Number(revision)
    : null;
}

function reportDocumentProtocol(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return (value as { outcome_protocol_version?: unknown })
    .outcome_protocol_version;
}

async function readReportBundleOnce(id: string): Promise<ReportBundle> {
  const [summary, cases, errors] = await Promise.all([
    getReportJson<ReportSummary>(id, 'summary.json', { strict: true }),
    getReportJson<ReportCases>(id, 'cases.json', { strict: true }),
    getReportJson<ReportErrorDigest>(id, 'error-digest.json', { strict: true }),
  ]);
  return { summary, cases, errors };
}

/**
 * Read a report bundle without ever returning a mixed canonical revision as exportable. A correction
 * can advance the pointer between the three requests, so a mismatch is retried at most twice and
 * then remains visibly changed until a later refetch obtains one coherent snapshot.
 */
export async function fetchReportBundle(
  id: string,
  expectedRevision?: number | null,
): Promise<ReportBundle> {
  let latest: ReportBundle = {
    summary: null,
    cases: null,
    errors: null,
    coherence: 'unavailable',
    reportRevision: expectedRevision ?? null,
  };
  for (let attempt = 0; attempt <= REPORT_BUNDLE_REFETCHES; attempt += 1) {
    const bundle = await readReportBundleOnce(id);
    const documents = [bundle.summary, bundle.cases, bundle.errors];
    const present = documents.filter((document) => document !== null);
    if (present.length === 0) {
      return {
        ...bundle,
        coherence: 'unavailable',
        reportRevision: expectedRevision ?? null,
      };
    }
    const protocols = present.map(reportDocumentProtocol);
    const legacy = protocols.every((version) => version === null);
    if (legacy && expectedRevision == null) {
      return { ...bundle, coherence: 'legacy', reportRevision: null };
    }
    const canonical = protocols.every((version) => version === 1);
    const revisions = present.map(reportDocumentRevision);
    const target =
      expectedRevision ?? revisions.find((value) => value !== null);
    const coherent =
      canonical &&
      target !== undefined &&
      target !== null &&
      revisions.every((revision) => revision === target);
    latest = {
      ...bundle,
      coherence: coherent ? 'coherent' : 'changed',
      reportRevision: target ?? null,
    };
    if (coherent) return latest;
  }
  return latest;
}

export async function correctOutcome(
  runId: string,
  caseId: string,
  body: OutcomeCorrectionBody,
): Promise<OutcomeCorrectionReceipt> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/cases/${encodeURIComponent(caseId)}/corrections`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await responseError(res, 'Failed to correct outcome');
  return (await res.json()) as OutcomeCorrectionReceipt;
}

export async function createOutcomeReassessment(
  runId: string,
  body: CreateOutcomeReassessmentBody,
): Promise<OutcomeReassessmentReceipt> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/outcome-reassessments`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await responseError(res, 'Failed to start AI analysis');
  return (await res.json()) as OutcomeReassessmentReceipt;
}

/**
 * Start an AI analysis for every eligible case of a run in one request. `batchId` is the batch's
 * idempotency key: the service derives each case's request id from it, so repeating a batch returns
 * the receipts it already created instead of starting a second analysis.
 */
export async function createBulkOutcomeReassessment(
  runId: string,
  body: CreateBulkOutcomeReassessmentBody,
): Promise<BulkOutcomeReassessmentResult> {
  const res = await apiFetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/outcome-reassessments/bulk`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await responseError(res, 'Failed to start AI analysis');
  return (await res.json()) as BulkOutcomeReassessmentResult;
}

export function getOutcomeReassessment(
  runId: string,
  requestId: string,
): Promise<OutcomeReassessmentReceipt> {
  return getJson(
    `/runs/${encodeURIComponent(runId)}/outcome-reassessments/${encodeURIComponent(requestId)}`,
    'Failed to load AI analysis',
  );
}

export function getOutcomeReassessmentEligibility(
  runId: string,
  caseId: string,
): Promise<OutcomeReassessmentEligibility> {
  return getJson(
    `/runs/${encodeURIComponent(runId)}/outcome-reassessments/eligibility?caseId=${encodeURIComponent(caseId)}`,
    'Failed to check AI analysis eligibility',
  );
}

export function listOutcomeReassessments(
  runId: string,
  caseId: string,
  limit = 20,
  offset = 0,
): Promise<{ items: OutcomeReassessmentReceipt[]; total: number }> {
  return getJson(
    `/runs/${encodeURIComponent(runId)}/outcome-reassessments?caseId=${encodeURIComponent(caseId)}&limit=${limit}&offset=${offset}`,
    'Failed to load AI analysis history',
  );
}

export async function loadOutcomeReassessmentState(
  runId: string,
  caseId: string,
  limit = 20,
): Promise<OutcomeReassessmentState> {
  const pageSize = Math.min(Math.max(limit, 1), 100);
  const [eligibility, firstPage] = await Promise.all([
    getOutcomeReassessmentEligibility(runId, caseId),
    listOutcomeReassessments(runId, caseId, pageSize),
  ]);
  const receipts = [...firstPage.items];
  const target = Math.min(limit, firstPage.total);
  while (receipts.length < target) {
    const page = await listOutcomeReassessments(
      runId,
      caseId,
      Math.min(100, target - receipts.length),
      receipts.length,
    );
    if (page.items.length === 0) break;
    receipts.push(...page.items);
  }
  const activeId = eligibility.active_request_id;
  let active = activeId
    ? (receipts.find((receipt) => receipt.request_id === activeId) ?? null)
    : (receipts.find(
        (receipt) =>
          receipt.status === 'queued' || receipt.status === 'running',
      ) ?? null);
  if (activeId && !active)
    active = await getOutcomeReassessment(runId, activeId);
  return { eligibility, receipts, active, total: firstPage.total };
}

/**
 * Raw bytes of a report file, for attaching to a chat verbatim (the typed getters above decode JSON;
 * the bridge needs the original blob). Bearer-authed; resolves to null on any failure so a missing
 * file never breaks the agent action.
 */
export async function fetchReportFileBlob(
  id: string,
  file: string,
): Promise<Blob | null> {
  try {
    const res = await apiFetch(
      `${API_BASE}/runs/${encodeURIComponent(id)}/report/${file}`,
      {
        headers: await headers(),
      },
    );
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

/**
 * Same-origin URL of a per-case artifact (screenshot, web video) by its run-relative S3 key.
 * The endpoint is bearer-authed, so a plain `<img src>`/`<video src>` (a sub-request that can't
 * carry the Authorization header) won't load — fetch the binary through `fetchArtifactBlob` and
 * expose it as an object URL instead (see `AuthMedia`).
 */
export function artifactUrl(id: string, key: string): string {
  return `${API_BASE}/runs/${encodeURIComponent(id)}/artifacts/${key}`;
}

/** Fetch a bearer-authed binary artifact as a Blob (consumed by AuthImage/AuthVideo). */
export async function fetchArtifactBlob(url: string): Promise<Blob> {
  const res = await apiFetch(url, { headers: await headers() });
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Failed to load artifact'),
    );
  }
  return res.blob();
}

/**
 * GET /runs/:id/api-log — every API call the run made, as one plain-text file (MEDA-914). Built by
 * the service on request from the stored call records; bearer-authed and visibility-scoped like
 * the run's other artifacts.
 */
export async function fetchRunApiLog(runId: string): Promise<Blob> {
  return fetchArtifactBlob(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/api-log`,
  );
}

/** Download all case-aligned results through the same authenticated gateway as the run. */
export async function fetchRunResultsExport(runId: string): Promise<Blob> {
  return fetchArtifactBlob(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/export.csv`,
  );
}

/**
 * Post-run generation Trace NDJSON (`generation/attempt-0/trace.jsonl` by default). Returns null
 * when the artifact is missing or unreadable so the Trace panel can hide without error spam.
 */
export async function fetchGenerationTrace(
  runId: string,
  relativeKey = 'generation/attempt-0/trace.jsonl',
): Promise<string | null> {
  return fetchArtifactText(runId, relativeKey);
}

/**
 * Bearer-authed text artifact by run-relative key. Returns null on 404 (or empty body) so
 * transparency panels stay quiet when artifacts are not written yet; other HTTP/network
 * failures propagate so auth/server problems stay visible in the query error state.
 */
export async function fetchArtifactText(
  runId: string,
  relativeKey: string,
): Promise<string | null> {
  try {
    const blob = await fetchArtifactBlob(artifactUrl(runId, relativeKey));
    const text = await blob.text();
    return text.trim() ? text : null;
  } catch (err) {
    if (err instanceof QualityApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * GET /runs/:id/generation-context — sources manifest + per-version seed/trace keys.
 * Returns null on 404 so Sources/Seed stay hidden until the endpoint ships; other failures
 * propagate.
 */
export async function fetchGenerationContext(
  runId: string,
): Promise<GenerationContextResponse | null> {
  try {
    const context = await getJson<GenerationContextResponse>(
      `/runs/${encodeURIComponent(runId)}/generation-context?projection=browser_v1`,
      'Failed to load generation context',
    );
    if (context.schema_version === 2) {
      generationBrowserContextSchema.parse(context);
      if (context.run_id !== runId)
        throw new Error('Generation context run identity changed');
    }
    return context;
  } catch (err) {
    if (err instanceof QualityApiError && err.status === 404) return null;
    throw err;
  }
}

// ── Query factories ──────────────────────────────────────────────────────────

export const qualityKeys = {
  capabilities: ['quality', 'capabilities'] as const,
  projects: ['quality', 'projects'] as const,
  project: (id: string) => ['quality', 'project', id] as const,
  launchOptions: (id: string) => ['quality', 'launch-options', id] as const,
  credentials: (projectId: string) =>
    ['quality', 'credentials', projectId] as const,
  deviceLeases: (projectId: string) =>
    ['quality', 'device-leases', projectId] as const,
  githubFolders: (projectId: string, repoUrl: string, branch: string) =>
    ['quality', 'github-folders', projectId, repoUrl, branch] as const,
  githubBranches: (projectId: string, repoUrl: string) =>
    ['quality', 'github-branches', projectId, repoUrl] as const,
  gitlabFolders: (projectId: string, repoUrl: string, branch: string) =>
    ['quality', 'gitlab-folders', projectId, repoUrl, branch] as const,
  gitlabBranches: (projectId: string, repoUrl: string) =>
    ['quality', 'gitlab-branches', projectId, repoUrl] as const,
  jiraProjects: (projectId: string) =>
    ['quality', 'jira-projects', projectId] as const,
  jiraLabels: (projectId: string, cloudId: string) =>
    ['quality', 'jira-labels', projectId, cloudId] as const,
  jiraIssueTypes: (projectId: string, cloudId: string, projectKey: string) =>
    ['quality', 'jira-issue-types', projectId, cloudId, projectKey] as const,
  jiraStatuses: (projectId: string, cloudId: string, projectKey: string) =>
    ['quality', 'jira-statuses', projectId, cloudId, projectKey] as const,
  confluenceSpaces: (projectId: string) =>
    ['quality', 'confluence-spaces', projectId] as const,
  testSuites: (
    projectId?: string,
    workspaceId?: string,
    workspaceProjectId?: string,
  ) =>
    [
      'quality',
      'test-suites',
      projectId ?? 'all',
      workspaceId ?? 'all',
      ...(workspaceProjectId ? [workspaceProjectId] : []),
    ] as const,
  lastGenerationRequest: (projectId: string) =>
    ['quality', 'last-generation-request', projectId] as const,
  regressionTargets: (projectId: string) =>
    ['quality', 'regression-targets', projectId] as const,
  localSuitesBase: ['quality', 'local-suites'] as const,
  localSuites: (workspaceId: string) =>
    ['quality', 'local-suites', workspaceId] as const,
  localCasesBase: ['quality', 'local-cases'] as const,
  localCases: (workspaceId: string, suiteId: string) =>
    ['quality', 'local-cases', workspaceId, suiteId] as const,
  testSuiteCases: (
    id: string,
    projectId?: string,
    source?: TestSource,
    sourceRef?: SourceRef,
  ) =>
    [
      'quality',
      'test-suite-cases',
      id,
      projectId ?? 'global',
      source ?? 'testrail',
      sourceRef ? JSON.stringify(sourceRef) : 'legacy-source',
    ] as const,
  config: ['quality', 'config'] as const,
  // Prefix for invalidating every runs query regardless of project/scope filter.
  runsBase: ['quality', 'runs'] as const,
  runs: (
    projectId?: string,
    scope?: RunScope,
    workspaceId?: string,
    includeArchived?: boolean,
  ) =>
    [
      'quality',
      'runs',
      projectId ?? 'all',
      scope ?? 'all',
      workspaceId ?? 'all',
      includeArchived ?? false,
    ] as const,
  // The board caches pages, the command palette caches one flat array. Same filters, different
  // shapes, so they cannot share a cache entry — whichever query ran first would hand the other
  // a value it cannot read. Still under `runsBase`, so one invalidation reaches both.
  runsInfinite: (
    projectId?: string,
    scope?: RunScope,
    workspaceId?: string,
    includeArchived?: boolean,
  ) =>
    [
      'quality',
      'runs',
      'infinite',
      projectId ?? 'all',
      scope ?? 'all',
      workspaceId ?? 'all',
      includeArchived ?? false,
    ] as const,
  run: (id: string) => ['quality', 'run', id] as const,
  finding: (id: string) => ['quality', 'finding', id] as const,
  logs: (id: string) => ['quality', 'logs', id] as const,
  generationTrace: (id: string, key: string) =>
    ['quality', 'generation-trace', id, key] as const,
  generationContext: (id: string) =>
    ['quality', 'generation-context', id] as const,
  structuredSeed: (id: string, key: string) =>
    ['quality', 'structured-seed', id, key] as const,
  artifactText: (id: string, key: string) =>
    ['quality', 'artifact-text', id, key] as const,
  proposals: (id: string) => ['quality', 'proposals', id] as const,
  caseRevisions: (id: string) => ['quality', 'case-revisions', id] as const,
  poorlyDescribed: (id: string) => ['quality', 'poorly-described', id] as const,
  inputs: (id: string) => ['quality', 'inputs', id] as const,
  authFlights: (id: string) => ['quality', 'auth-flights', id] as const,
  reportBundleBase: (id: string) => ['quality', 'report-bundle', id] as const,
  reportBundle: (id: string, revision?: number | null) =>
    ['quality', 'report-bundle', id, revision ?? 'current'] as const,
  outcomeReassessment: (runId: string, caseId: string) =>
    ['quality', 'outcome-reassessment', runId, caseId] as const,
  /** Every case's reassessment state for one run. A run-wide analysis has to invalidate all of
   *  them, and react-query matches by key prefix, so one entry covers every row. */
  outcomeReassessmentsBase: (runId: string) =>
    ['quality', 'outcome-reassessment', runId] as const,
  exploratoryArtifacts: ['quality', 'exploratory-artifact'] as const,
  exploratoryArtifact: (runId: string) =>
    ['quality', 'exploratory-artifact', runId] as const,
  testRailSections: (
    externalProjectId: string,
    suiteId?: number,
    sectionIdHint?: number,
    workspaceId?: string,
  ) =>
    [
      'quality',
      'testrail-sections',
      externalProjectId,
      suiteId,
      sectionIdHint,
      workspaceId ?? 'all',
    ] as const,
  zephyrFolders: (projectId: string) =>
    ['quality', 'zephyr-folders', projectId] as const,
  xrayFolders: (projectId: string, workspaceId?: string) =>
    ['quality', 'xray-folders', projectId, workspaceId ?? 'all'] as const,
  regressionBase: ['quality', 'regression'] as const,
  regressionJobs: (projectId?: string) =>
    ['quality', 'regression', 'list', projectId ?? 'all'] as const,
  regressionJob: (id: string) => ['quality', 'regression', 'job', id] as const,
  regressionJobRuns: (id: string) =>
    ['quality', 'regression', 'runs', id] as const,
  runPreflight: (
    projectId: string,
    body: RunPreflightBody,
    settingsUpdatedAt: string | undefined,
  ) =>
    [
      'quality',
      'run-preflight',
      projectId,
      body,
      settingsUpdatedAt ?? null,
    ] as const,
  caseAdaptation: (
    caseId: string,
    workspaceId: string,
    sourceRunId?: string,
    sourceRef?: SourceRef,
  ) =>
    [
      'quality',
      'case-adaptation',
      workspaceId,
      sourceRunId ?? 'direct-suite',
      caseId,
      sourceRef ? JSON.stringify(sourceRef) : 'legacy-source',
    ] as const,
  suiteExecutionContext: (
    suiteId: string,
    workspaceId: string,
    sourceRef?: SourceRef,
    contextRef?: AdaptationContextRef | AdaptationContextState | null,
  ) =>
    [
      'quality',
      'suite-execution-context',
      workspaceId,
      suiteId,
      sourceRef ? JSON.stringify(sourceRef) : 'legacy-source',
      contextRef ? JSON.stringify(contextRef) : 'legacy-context',
    ] as const,
  runLaunch: (id: string) => ['quality', 'run-launch', id] as const,
};

/** Kept as the single compatibility point while the service's source-run route lands. */
export async function fetchExploratoryArtifactByRun(
  runId: string,
): Promise<ExploratoryArtifactRead | null> {
  try {
    return normalizeExploratoryArtifact(
      await getJson<ExploratoryArtifactRead>(
        `/exploratory-artifacts/by-run/${encodeURIComponent(runId)}`,
        'Failed to load exploratory artifacts',
      ),
    );
  } catch (error) {
    if (error instanceof QualityApiError && error.status === 404) return null;
    throw error;
  }
}

/** Revision container names are snake case, while the nested DTO contracts are camel
 * case: the service serializer emits `story_verdicts` and `manual_refs` verbatim from
 * entity fields whose own keys stay camel case. Fill the collections the UI iterates so
 * a revision saved before they existed still renders. */
function normalizeExploratoryArtifact(
  response: ExploratoryArtifactRead,
): ExploratoryArtifactRead {
  return {
    ...response,
    revisions: response.revisions.map((revision) => ({
      ...revision,
      story_verdicts: revision.story_verdicts ?? [],
      manual_refs: {
        manualStoryRefs: revision.manual_refs?.manualStoryRefs ?? [],
        manualCaseRefs: revision.manual_refs?.manualCaseRefs ?? [],
      },
    })),
  };
}

export function exploratoryArtifactQueryOptions(runId: string, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.exploratoryArtifact(runId),
    queryFn: () => fetchExploratoryArtifactByRun(runId),
    enabled: enabled && runId.length > 0,
    staleTime: 5_000,
  });
}

async function mutateExploratoryArtifact<T>(
  path: string,
  method: 'PATCH' | 'POST',
  body: unknown,
): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method,
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok)
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Exploratory artifact action failed'),
    );
  return (await res.json()) as T;
}

export function reviseExploratoryArtifact(
  artifactId: string,
  body: { expected_revision_id: string } & Partial<
    Pick<
      ExploratoryRevision,
      'draft_cases' | 'story_verdicts' | 'manual_refs' | 'explicit_links'
    >
  >,
) {
  return mutateExploratoryArtifact<ExploratoryRevision>(
    `/exploratory-artifacts/${encodeURIComponent(artifactId)}/revisions`,
    'PATCH',
    {
      expectedRevisionId: body.expected_revision_id,
      draftCases: body.draft_cases,
      storyVerdicts: body.story_verdicts,
      manualRefs: body.manual_refs && {
        manualStoryRefs: body.manual_refs.manualStoryRefs,
        manualCaseRefs: body.manual_refs.manualCaseRefs,
      },
      explicitLinks: body.explicit_links,
    },
  );
}

export function confirmExploratoryPublication(
  artifactId: string,
  body: {
    revisionId: string;
    operation: 'create' | 'update';
    draftCaseIndex: number;
    destination: {
      connector: string;
      projectRef?: string;
      containerRef?: string;
      caseRef?: string;
    };
  },
) {
  return mutateExploratoryArtifact<ExploratoryPublicationAttempt>(
    `/exploratory-artifacts/${encodeURIComponent(artifactId)}/publications/confirm`,
    'POST',
    body,
  );
}

export function retryExploratoryPublication(
  artifactId: string,
  attemptId: string,
) {
  return mutateExploratoryArtifact<ExploratoryPublicationAttempt>(
    `/exploratory-artifacts/${encodeURIComponent(artifactId)}/publications/retry`,
    'POST',
    { attemptId },
  );
}

/**
 * The operator's assertion of what happened outside the connector for an
 * attempt that requires manual publication: either they created or updated
 * the case by hand (and can name it), or they did not publish it at all.
 * `externalCaseId` is required when `outcome` is `'published'`.
 */
export type ExploratoryManualReconciliation =
  | { outcome: 'published'; externalCaseId: string }
  | { outcome: 'not_published'; externalCaseId?: never };

export function reconcileExploratoryPublication(
  artifactId: string,
  attemptId: string,
  manualOutcome?: ExploratoryManualReconciliation,
) {
  return mutateExploratoryArtifact<ExploratoryPublicationAttempt>(
    `/exploratory-artifacts/${encodeURIComponent(artifactId)}/publications/reconcile`,
    'POST',
    { attemptId, ...manualOutcome },
  );
}

/**
 * True when a connector has no automated write path and the attempt is still
 * waiting on the operator to publish the case by hand and record the
 * outcome. Checks both `outcome_evidence.reason` and `state === 'in_flight'`
 * — after reconciliation the attempt moves to `'succeeded'` (recorded under
 * `external_ids.manualCaseRef`, deliberately distinct from a
 * connector-confirmed id) or `'failed'`, so this naturally goes false and
 * the hint retires on its own once the operator has closed the attempt out.
 */
export function isManualPublicationRequired(
  attempt: ExploratoryPublicationAttempt,
): boolean {
  return (
    attempt.state === 'in_flight' &&
    attempt.outcome_evidence?.reason === 'manual_publication_required'
  );
}

export function runProposalsQueryOptions(id: string, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.proposals(id),
    queryFn: () => fetchRunProposals(id),
    enabled: enabled && id.length > 0,
    staleTime: 5_000,
  });
}

/** Poorly-described-case findings for a run (MEDA-352). Enabled only for a terminal, failed run.
 *  The post-run analyzer writes these findings asynchronously AFTER the run flips terminal, and the
 *  SSE stream is off by then, so `refetchInterval` polls briefly while the list is still empty to pick
 *  up findings that land just after the status change. It stops as soon as any finding arrives, and is
 *  bounded (`dataUpdateCount`) so a failed run with genuinely no flagged cases stops polling after the
 *  analyzer window (~1 min) rather than forever. */
export function poorlyDescribedFindingsQueryOptions(
  id: string,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualityKeys.poorlyDescribed(id),
    queryFn: () => fetchPoorlyDescribedFindings(id),
    enabled: enabled && id.length > 0,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const empty = !Array.isArray(data) || data.length === 0;
      return empty && query.state.dataUpdateCount < 12 ? 5_000 : false;
    },
    refetchIntervalInBackground: true,
  });
}

/** A run's refine revisions (MEDA-352). The `revision.updated` SSE event invalidates this key live
 *  while a run streams; on a terminal run the stream is off, so `refetchInterval` polls while any
 *  revision is still `pending` — the worker's rewrite lands asynchronously via /internal/refine-result,
 *  so this surfaces the proposed/failed transition without a manual reload, and stops once nothing is
 *  pending (mirrors pendingInputsQueryOptions' SSE safety-net poll). */
export function caseRevisionsQueryOptions(id: string, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.caseRevisions(id),
    queryFn: () => fetchCaseRevisions(id),
    enabled: enabled && id.length > 0,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      return Array.isArray(data) && data.some((r) => r.status === 'pending')
        ? 4_000
        : false;
    },
    // Keep polling while pending even if the user tabs away — the rewrite lands async (10–30s) and a
    // backgrounded tab must still pick up the proposed/failed transition.
    refetchIntervalInBackground: true,
  });
}

/** Query options for a run's pending input requests. `enabled` gates it to non-terminal runs — the
 *  SSE `input.required`/`input.resolved` events invalidate this key live (quality-run-stream.ts).
 *  `refetchInterval` is a safety net (mirrors runsQueryOptions) in case the per-run SSE stream drops
 *  or an `input.*` event is missed — SSE stays the fast path, polling just bounds the staleness. */
export function pendingInputsQueryOptions(id: string, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.inputs(id),
    queryFn: () => fetchPendingInputs(id),
    enabled: enabled && id.length > 0,
    staleTime: 5_000,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Flight states that still represent an OPEN WAIT, i.e. the ones worth polling for: the run cannot
 * move until one of them resolves. `account_mismatch` belongs here — the panel treats it as an
 * active wait, and leaving it out stopped the poll on a run whose only remaining flight was a
 * mismatch, so a missed SSE event left a stale countdown and a live Apply form past expiry.
 *
 * Deliberately NOT the same set the panel renders: that one also includes `expired`, because an
 * expired flight must stay on screen to explain why the run failed. Rendered ≠ still waiting.
 */
export const ACTIVE_AUTH_FLIGHT_STATES = new Set<AuthFlight['state']>([
  'pending',
  'applying',
  'account_mismatch',
]);

/** Auth flights stay readable after terminal expiry so the final failed state remains explainable.
 * Focus/reconnect refetches are explicit because this app disables them in the global QueryClient. */
export function authFlightsQueryOptions(id: string, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.authFlights(id),
    queryFn: () => fetchAuthFlights(id),
    enabled: enabled && id.length > 0,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: (query) => {
      const flights = query.state.data;
      return Array.isArray(flights) &&
        flights.some((flight) => ACTIVE_AUTH_FLIGHT_STATES.has(flight.state))
        ? 5_000
        : false;
    },
  });
}

/**
 * Poll the active device takeover.
 *
 * 5 s is right for a doorbell but useless for DRIVING a device: every gesture's result and the live
 * "now" value of the verified control arrive through this poll, and a human waiting five seconds to see
 * whether their drag moved the wheel cannot work. So the interval drops to 1 s while a takeover is
 * actually open and returns to the doorbell rate when there is none.
 */
export function activeTakeoverQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...qualityKeys.inputs(id), 'takeover'],
    queryFn: () => fetchActiveTakeover(id),
    enabled: id.length > 0,
    refetchInterval: (query) =>
      query.state.data && query.state.data.state !== 'idle' ? 1_000 : 5_000,
  });
}

export function qualityProjectsQueryOptions() {
  return queryOptions({
    queryKey: qualityKeys.projects,
    queryFn: fetchProjects,
    staleTime: 60_000,
  });
}

export function qualityCapabilitiesQueryOptions() {
  return queryOptions({
    queryKey: qualityKeys.capabilities,
    queryFn: fetchQualityCapabilities,
    staleTime: 60_000,
    retry: 0,
  });
}

export function projectLaunchOptionsQueryOptions(
  projectId: string,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualityKeys.launchOptions(projectId),
    queryFn: () => fetchProjectLaunchOptions(projectId),
    enabled: enabled && projectId.length > 0,
    staleTime: 30_000,
    retry: 0,
  });
}

export function useQualityCapabilities() {
  return useQuery(qualityCapabilitiesQueryOptions());
}

export function useProjectLaunchOptions(projectId: string, enabled = true) {
  return useQuery(projectLaunchOptionsQueryOptions(projectId, enabled));
}

/** One replay finding by id — the display data behind a chat finding card + the deep-link handoff. */
export async function fetchFinding(id: string): Promise<ReplayFinding> {
  return getJson<ReplayFinding>(
    `/findings/${encodeURIComponent(id)}`,
    'Failed to load finding',
  );
}

export function findingQueryOptions(id: string) {
  return queryOptions({
    queryKey: qualityKeys.finding(id),
    queryFn: () => fetchFinding(id),
    enabled: id.length > 0,
    staleTime: 30_000,
    retry: 1,
  });
}

export function projectQueryOptions(id: string) {
  return queryOptions({
    queryKey: qualityKeys.project(id),
    queryFn: () => fetchProject(id),
    enabled: id.length > 0,
    staleTime: 30_000,
    retry: 1,
  });
}

export function credentialsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: qualityKeys.credentials(projectId),
    queryFn: () => fetchCredentials(projectId),
    staleTime: 30_000,
    enabled: projectId.length > 0,
    retry: 1,
  });
}

export function deviceLeasesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: qualityKeys.deviceLeases(projectId),
    queryFn: () => fetchDeviceLeases(projectId),
    staleTime: 10_000,
    enabled: projectId.length > 0,
    retry: 1,
  });
}

export function useBreakDeviceLease(
  projectId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (udid: string) => breakDeviceLease(projectId, udid),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: qualityKeys.deviceLeases(projectId),
      });
    },
  });
}

/**
 * A project's configured environments (Block C), read from its cached settings via the existing
 * project query — no separate fetch/query key. `environments` is `[]` while the project is loading,
 * errored, or has no environments configured yet (so a consumer never has to handle `undefined`);
 * `isLoading`/`isError` expose the query state so a consumer can distinguish "still loading" from
 * "no environments" — e.g. to avoid flashing a stored value as an unknown environment during load.
 * `defaultEnvironmentName` is the project's UI pre-selection (undefined when unset or `settings` is
 * masked for a non-admin viewer — the same visibility this whole hook already depends on).
 */
export function useProjectEnvironments(projectId: string): {
  environments: EnvironmentConfig[];
  projectId: string | undefined;
  defaultAppUrl: string | undefined;
  defaultEnvironmentName: string | undefined;
  /** The project's `updated_at` — changes whenever an environment (or any other setting) is created,
   *  edited, or deleted. Feed it into {@link runPreflightQueryOptions} so the preflight cache busts on
   *  a settings change even when `environmentName` itself didn't change. */
  settingsUpdatedAt: string | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery(projectQueryOptions(projectId));
  return {
    projectId: query.data?.id,
    defaultAppUrl: query.data?.capabilities?.web?.appUrl,
    environments: query.data?.settings?.environments ?? [],
    defaultEnvironmentName: query.data?.settings?.defaultEnvironmentName,
    settingsUpdatedAt: query.data?.updated_at,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** A project's credentials (metadata only), via the existing credentials query — for pickers (like an
 *  environment's "Auto-login" binding) that only need the plain list, not loading/error state. */
export function useProjectCredentials(projectId: string): ProjectCredential[] {
  const query = useQuery(credentialsQueryOptions(projectId));
  return query.data ?? [];
}

// Folders (nested) of a project's GitHub repo on a branch, for the generation folder picker (mirrors
// the TestRail-section dropdown). Empty branch = the repo's default branch. Returns [] when no token
// is set or the repo/branch can't be read.
export async function fetchGithubFolders(
  projectId: string,
  repoUrl: string,
  branch: string,
): Promise<string[]> {
  const branchParam = branch ? `&branch=${encodeURIComponent(branch)}` : '';
  const data = await getJson<{ folders: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/github-folders?repoUrl=${encodeURIComponent(repoUrl)}${branchParam}`,
    'Failed to load repository folders',
  );
  return data.folders;
}

export function githubFoldersQueryOptions(
  projectId: string,
  repoUrl: string,
  branch: string,
) {
  return queryOptions({
    queryKey: qualityKeys.githubFolders(projectId, repoUrl, branch),
    queryFn: () => fetchGithubFolders(projectId, repoUrl, branch),
    staleTime: 60_000,
    enabled: projectId.length > 0 && repoUrl.trim().length > 0,
    retry: 1,
  });
}

// Branch names of a project's GitHub repo, for the generation branch picker. Returns [] when no
// token is set or the repo can't be read.
export async function fetchGithubBranches(
  projectId: string,
  repoUrl: string,
): Promise<string[]> {
  const data = await getJson<{ branches: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/github-branches?repoUrl=${encodeURIComponent(repoUrl)}`,
    'Failed to load repository branches',
  );
  return data.branches;
}

export function githubBranchesQueryOptions(projectId: string, repoUrl: string) {
  return queryOptions({
    queryKey: qualityKeys.githubBranches(projectId, repoUrl),
    queryFn: () => fetchGithubBranches(projectId, repoUrl),
    staleTime: 60_000,
    enabled: projectId.length > 0 && repoUrl.trim().length > 0,
    retry: 1,
  });
}

// GitLab folder/branch pickers — the GitLab twins of the GitHub fetchers above.
export async function fetchGitlabFolders(
  projectId: string,
  repoUrl: string,
  branch: string,
): Promise<string[]> {
  const branchParam = branch ? `&branch=${encodeURIComponent(branch)}` : '';
  const data = await getJson<{ folders: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/gitlab-folders?repoUrl=${encodeURIComponent(repoUrl)}${branchParam}`,
    'Failed to load project folders',
  );
  return data.folders;
}

export function gitlabFoldersQueryOptions(
  projectId: string,
  repoUrl: string,
  branch: string,
) {
  return queryOptions({
    queryKey: qualityKeys.gitlabFolders(projectId, repoUrl, branch),
    queryFn: () => fetchGitlabFolders(projectId, repoUrl, branch),
    staleTime: 60_000,
    enabled: projectId.length > 0 && repoUrl.trim().length > 0,
    retry: 1,
  });
}

export async function fetchGitlabBranches(
  projectId: string,
  repoUrl: string,
): Promise<string[]> {
  const data = await getJson<{ branches: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/gitlab-branches?repoUrl=${encodeURIComponent(repoUrl)}`,
    'Failed to load project branches',
  );
  return data.branches;
}

export function gitlabBranchesQueryOptions(projectId: string, repoUrl: string) {
  return queryOptions({
    queryKey: qualityKeys.gitlabBranches(projectId, repoUrl),
    queryFn: () => fetchGitlabBranches(projectId, repoUrl),
    staleTime: 60_000,
    enabled: projectId.length > 0 && repoUrl.trim().length > 0,
    retry: 1,
  });
}

// ── Jira pickers (per-user OAuth via the connector) ──────────────────────────
// Unlike GitHub/GitLab, Jira has no per-project token or allowlist: the endpoints use the caller's own
// OAuth token (resolved server-side) and return the user's own projects/labels/etc. Enabled only when
// the user has connected Jira (the panel gates on the connector status).

/** One Jira project the user can pick, tagged with the Atlassian site it belongs to. */
export interface JiraProjectRef {
  cloudId: string;
  site: string;
  key: string;
  name: string;
}

/** The per-run Jira selection sent to the backend. A raw JQL is the scope (it can name its own
 *  project); the instance + token are resolved server-side. */
export interface JiraRunSourceInput {
  jql: string;
  includeComments?: boolean;
}

/** Result of a "Test query" button — the match count + a small sample, or a clear error. Shared by
 *  the Jira (JQL) and Confluence (CQL) sources. */
export interface QueryTestResult {
  ok: boolean;
  total?: number;
  sample?: string[];
  error?: string;
}

/** Validate a JQL query against the caller's connected Jira and report how many issues match. */
export async function testProjectJiraQuery(
  projectId: string,
  jql: string,
): Promise<QueryTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/jira-test-query`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ jql }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Test failed'),
    );
  }
  return (await res.json()) as QueryTestResult;
}

/** Validate a CQL query against the caller's connected Confluence and report how many pages match. */
export async function testProjectConfluenceQuery(
  projectId: string,
  cql: string,
): Promise<QueryTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/confluence-test-query`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ cql }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Test failed'),
    );
  }
  return (await res.json()) as QueryTestResult;
}

/** Validate a WIQL query against the project's linked Azure DevOps account and report how many work
 *  items match. */
export async function testProjectAzureBoardsQuery(
  projectId: string,
  wiql: string,
): Promise<QueryTestResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/azure-boards-test-query`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ wiql }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Test failed'),
    );
  }
  return (await res.json()) as QueryTestResult;
}

/** One source a prompt resolved to: what will be read, and how much of it matches. */
export interface PromptResolveSource {
  label: string;
  query: string;
  total?: number;
  sample?: string[];
  error?: string;
}

/** The "Check" answer: what the prompt was understood to mean, before a run reads anything. */
export interface PromptResolveResult {
  ok: boolean;
  error?: string;
  warnings: string[];
  unresolved: string[];
  sources: PromptResolveSource[];
}

export async function resolveProjectJiraPrompt(
  projectId: string,
  prompt: string,
): Promise<PromptResolveResult> {
  return resolvePrompt(projectId, 'jira', prompt);
}

export async function resolveProjectConfluencePrompt(
  projectId: string,
  prompt: string,
): Promise<PromptResolveResult> {
  return resolvePrompt(projectId, 'confluence', prompt);
}

async function resolvePrompt(
  projectId: string,
  connector: 'jira' | 'confluence',
  prompt: string,
): Promise<PromptResolveResult> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/${connector}-resolve-prompt`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ prompt }),
    },
  );
  if (!res.ok) {
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Check failed'),
    );
  }
  return (await res.json()) as PromptResolveResult;
}

export async function fetchJiraProjects(
  projectId: string,
): Promise<JiraProjectRef[]> {
  const data = await getJson<{ projects: JiraProjectRef[] }>(
    `/projects/${encodeURIComponent(projectId)}/jira-projects`,
    'Failed to load Jira projects',
  );
  return data.projects;
}

export function jiraProjectsQueryOptions(projectId: string, enabled = true) {
  return queryOptions({
    queryKey: qualityKeys.jiraProjects(projectId),
    queryFn: () => fetchJiraProjects(projectId),
    staleTime: 60_000,
    enabled: enabled && projectId.length > 0,
    retry: 1,
  });
}

export async function fetchJiraLabels(
  projectId: string,
  cloudId: string,
): Promise<string[]> {
  const data = await getJson<{ labels: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/jira-labels?cloudId=${encodeURIComponent(cloudId)}`,
    'Failed to load Jira labels',
  );
  return data.labels;
}

export function jiraLabelsQueryOptions(projectId: string, cloudId: string) {
  return queryOptions({
    queryKey: qualityKeys.jiraLabels(projectId, cloudId),
    queryFn: () => fetchJiraLabels(projectId, cloudId),
    staleTime: 60_000,
    enabled: projectId.length > 0 && cloudId.length > 0,
    retry: 1,
  });
}

export async function fetchJiraIssueTypes(
  projectId: string,
  cloudId: string,
  projectKey: string,
): Promise<string[]> {
  const data = await getJson<{ issueTypes: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/jira-issue-types?cloudId=${encodeURIComponent(cloudId)}&projectKey=${encodeURIComponent(projectKey)}`,
    'Failed to load Jira issue types',
  );
  return data.issueTypes;
}

export function jiraIssueTypesQueryOptions(
  projectId: string,
  cloudId: string,
  projectKey: string,
) {
  return queryOptions({
    queryKey: qualityKeys.jiraIssueTypes(projectId, cloudId, projectKey),
    queryFn: () => fetchJiraIssueTypes(projectId, cloudId, projectKey),
    staleTime: 60_000,
    enabled:
      projectId.length > 0 && cloudId.length > 0 && projectKey.length > 0,
    retry: 1,
  });
}

export async function fetchJiraStatuses(
  projectId: string,
  cloudId: string,
  projectKey: string,
): Promise<string[]> {
  const data = await getJson<{ statuses: string[] }>(
    `/projects/${encodeURIComponent(projectId)}/jira-statuses?cloudId=${encodeURIComponent(cloudId)}&projectKey=${encodeURIComponent(projectKey)}`,
    'Failed to load Jira statuses',
  );
  return data.statuses;
}

export function jiraStatusesQueryOptions(
  projectId: string,
  cloudId: string,
  projectKey: string,
) {
  return queryOptions({
    queryKey: qualityKeys.jiraStatuses(projectId, cloudId, projectKey),
    queryFn: () => fetchJiraStatuses(projectId, cloudId, projectKey),
    staleTime: 60_000,
    enabled:
      projectId.length > 0 && cloudId.length > 0 && projectKey.length > 0,
    retry: 1,
  });
}

// ── Confluence pickers (per-user OAuth via the connector) ────────────────────
// The Confluence twin of the Jira pickers: the caller's own OAuth token (server-side) returns their
// own spaces. Labels have no clean list endpoint in Confluence Cloud, so labels are free-text on the
// client and CQL covers everything else — no label/type/status pickers here.

/** One Confluence space the user can pick, tagged with the Atlassian site it belongs to. */
export interface ConfluenceSpaceRef {
  cloudId: string;
  site: string;
  key: string;
  name: string;
}

/** The per-run Confluence selection sent to the backend. A raw CQL is the scope (it can name its own
 *  space); the instance + token are resolved server-side. */
export interface ConfluenceRunSourceInput {
  cql: string;
  includeComments?: boolean;
}

export async function fetchConfluenceSpaces(
  projectId: string,
): Promise<ConfluenceSpaceRef[]> {
  const data = await getJson<{ spaces: ConfluenceSpaceRef[] }>(
    `/projects/${encodeURIComponent(projectId)}/confluence-spaces`,
    'Failed to load Confluence spaces',
  );
  return data.spaces;
}

export function confluenceSpacesQueryOptions(
  projectId: string,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualityKeys.confluenceSpaces(projectId),
    queryFn: () => fetchConfluenceSpaces(projectId),
    staleTime: 60_000,
    enabled: enabled && projectId.length > 0,
    retry: 1,
  });
}

export function testSuitesQueryOptions(
  projectId?: string,
  workspaceId?: string,
  workspaceProjectId?: string,
) {
  return queryOptions({
    queryKey: qualityKeys.testSuites(
      projectId,
      workspaceId,
      workspaceProjectId,
    ),
    queryFn: () => fetchTestSuites(projectId, workspaceId, workspaceProjectId),
    staleTime: 30_000,
    // TestRail is slow (~2s/request); avoid the default 3-retry fan-out amplifying load on failure.
    retry: 1,
  });
}

export function lastGenerationRequestQueryOptions(
  projectId: string | undefined,
) {
  return queryOptions({
    queryKey: qualityKeys.lastGenerationRequest(projectId ?? ''),
    queryFn: () => fetchLastGenerationRequest(projectId ?? ''),
    enabled: Boolean(projectId),
    staleTime: 30_000,
    retry: 1,
  });
}

export function regressionTargetsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: qualityKeys.regressionTargets(projectId),
    queryFn: () => fetchRegressionTargets(projectId),
    staleTime: 30_000,
    retry: 1,
  });
}

export function testSuiteCasesQueryOptions(
  id: string,
  projectId?: string,
  source?: TestSource,
  sourceRef?: SourceRef,
) {
  return queryOptions({
    queryKey: qualityKeys.testSuiteCases(id, projectId, source, sourceRef),
    queryFn: () =>
      fetchTestSuiteCases(id, projectId, source, undefined, sourceRef),
    // Case specs change rarely; keep them warm so re-opening the page is instant.
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function localSuitesQueryOptions(workspaceId: string | undefined) {
  return queryOptions({
    queryKey: qualityKeys.localSuites(workspaceId ?? ''),
    queryFn: () => fetchLocalSuites(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    retry: 1,
  });
}

export function localCasesQueryOptions(
  workspaceId: string | undefined,
  suiteId: string,
) {
  return queryOptions({
    queryKey: qualityKeys.localCases(workspaceId ?? '', suiteId),
    queryFn: () => fetchAllLocalCases(workspaceId ?? '', suiteId),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Wraps a local store write so every screen that shows stored suites refreshes after it: the store's
 * own lists and the Tests tab, whose rows and counts come from `/test-suites`.
 */
function useLocalStoreMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
): UseMutationResult<TResult, Error, TVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: qualityKeys.localSuitesBase,
      });
      void queryClient.invalidateQueries({
        queryKey: qualityKeys.localCasesBase,
      });
      void queryClient.invalidateQueries({
        queryKey: ['quality', 'test-suites'],
      });
    },
  });
}

export function useCreateLocalSuite(workspaceId: string) {
  return useLocalStoreMutation(
    (
      input: string | { name: string; requestAuthority: ApiRequestAuthority },
    ) =>
      typeof input === 'string'
        ? createLocalSuite(workspaceId, input)
        : createLocalSuite(workspaceId, input.name, input.requestAuthority),
  );
}

export function useRenameLocalSuite(workspaceId: string, suiteId: string) {
  return useLocalStoreMutation((name: string) =>
    renameLocalSuite(workspaceId, suiteId, name),
  );
}

export function useDeleteLocalSuite(workspaceId: string, suiteId: string) {
  return useLocalStoreMutation((expectedCaseCount: number) =>
    deleteLocalSuite(workspaceId, suiteId, expectedCaseCount),
  );
}

export function useCreateLocalCases(workspaceId: string, suiteId: string) {
  return useLocalStoreMutation((cases: LocalCaseBody[]) =>
    createLocalCases(workspaceId, suiteId, cases),
  );
}

export function useUpdateLocalCase(workspaceId: string) {
  return useLocalStoreMutation(
    (variables: {
      caseId: string;
      expectedRevision: number;
      changes: Partial<LocalCaseBody>;
    }) =>
      updateLocalCase(
        workspaceId,
        variables.caseId,
        variables.expectedRevision,
        variables.changes,
      ),
  );
}

export function useDeleteLocalCase(workspaceId: string) {
  return useLocalStoreMutation(
    (variables: { caseId: string; expectedRevision: number }) =>
      deleteLocalCase(
        workspaceId,
        variables.caseId,
        variables.expectedRevision,
      ),
  );
}

export function useReorderLocalCases(workspaceId: string, suiteId: string) {
  return useLocalStoreMutation((caseIds: string[]) =>
    reorderLocalCases(workspaceId, suiteId, caseIds),
  );
}

// The endpoint takes the INTERNAL CTI project id (UUID): it checks access, then resolves the
// TestRail source-link server-side. (Sending the external TestRail id under `?project=` silently
// yields no sections.)
// `suiteId` picks the suite whose sections are read. Without it the server reads the suite of
// `sectionIdHint` (an already chosen section), else the default suite. Empty responses carry only
// `sections`, so the suite fields fall back to "no suites".
async function fetchTestRailSections(
  projectId: string,
  workspaceId?: string,
  suiteId?: number,
  sectionIdHint?: number,
): Promise<TestRailSectionTree> {
  const params = new URLSearchParams({ projectId });
  if (workspaceId) params.set('workspace_id', workspaceId);
  if (suiteId !== undefined) params.set('suite_id', String(suiteId));
  if (sectionIdHint !== undefined)
    params.set('section_id', String(sectionIdHint));
  const data = await getJson<Partial<TestRailSectionTree>>(
    `/config/testrail/sections?${params.toString()}`,
    'Failed to load TestRail sections',
  );
  return {
    sections: data.sections ?? [],
    suites: data.suites ?? [],
    suiteId: data.suiteId ?? null,
  };
}

export function testRailSectionsQueryOptions(
  projectId: string,
  workspaceId?: string,
  suiteId?: number,
  sectionIdHint?: number,
) {
  return queryOptions({
    queryKey: qualityKeys.testRailSections(
      projectId,
      suiteId,
      sectionIdHint,
      workspaceId,
    ),
    queryFn: () =>
      fetchTestRailSections(projectId, workspaceId, suiteId, sectionIdHint),
    staleTime: 60_000,
    retry: 1,
    enabled: projectId.length > 0,
  });
}

// The Zephyr twin of fetchTestRailSections: takes the INTERNAL CTI project id (UUID), checks access,
// then resolves the Zephyr source-link server-side. Empty list when no link/access; 422 when the
// project has no Zephyr account.
async function fetchZephyrFolders(
  projectId: string,
  workspaceId?: string,
): Promise<ZephyrFolder[]> {
  const params = new URLSearchParams({ projectId });
  if (workspaceId) params.set('workspace_id', workspaceId);
  const data = await getJson<{ folders: ZephyrFolder[] }>(
    `/config/zephyr/folders?${params.toString()}`,
    'Failed to load Zephyr folders',
  );
  return data.folders;
}

export function zephyrFoldersQueryOptions(
  projectId: string,
  workspaceId?: string,
) {
  return queryOptions({
    queryKey: qualityKeys.zephyrFolders(projectId),
    queryFn: () => fetchZephyrFolders(projectId, workspaceId),
    staleTime: 60_000,
    retry: 1,
    enabled: projectId.length > 0,
  });
}

/** The Xray twin of fetchZephyrFolders: takes the INTERNAL CTI project id (UUID), checks access, then
 *  resolves the Xray source-link server-side. Empty list when no link/access; 422 when the project has
 *  no Xray account. */
async function fetchXrayFolders(
  projectId: string,
  workspaceId?: string,
): Promise<XrayFolder[]> {
  const params = new URLSearchParams({ projectId });
  if (workspaceId) params.set('workspace_id', workspaceId);
  const data = await getJson<{ folders: XrayFolder[] }>(
    `/config/xray/folders?${params.toString()}`,
    'Failed to load Xray folders',
  );
  return data.folders;
}

export function xrayFoldersQueryOptions(
  projectId: string,
  workspaceId?: string,
) {
  return queryOptions({
    queryKey: qualityKeys.xrayFolders(projectId, workspaceId),
    queryFn: () => fetchXrayFolders(projectId, workspaceId),
    staleTime: 60_000,
    retry: 1,
    enabled: projectId.length > 0,
  });
}

export function runsQueryOptions(
  params: {
    projectId?: string;
    scope?: RunScope;
    workspaceId?: string;
    includeArchived?: boolean;
  } = {},
) {
  return queryOptions({
    queryKey: qualityKeys.runs(
      params.projectId,
      params.scope,
      params.workspaceId,
      params.includeArchived,
    ),
    queryFn: () => fetchRuns(params),
    staleTime: 10_000,
    // Poll the board as a fallback — no per-run SSE stream is open here, so
    // without this the list goes stale until the user opens a run detail.
    refetchInterval: 5_000,
  });
}

/** Runs read per page. The service defaults to the same number when a caller sends no limit. */
export const RUNS_PAGE_SIZE = 50;

/**
 * The runs board, paged newest first so the whole history is reachable rather than the newest 50.
 *
 * Only the first page keeps the poll. Refetching every loaded page costs a request per page, and a
 * run created meanwhile shifts every offset under it, which makes rows duplicate or disappear while
 * the user is reading further back.
 */
export function runsInfiniteQueryOptions(
  params: {
    projectId?: string;
    scope?: RunScope;
    workspaceId?: string;
    includeArchived?: boolean;
  } = {},
) {
  return infiniteQueryOptions({
    queryKey: qualityKeys.runsInfinite(
      params.projectId,
      params.scope,
      params.workspaceId,
      params.includeArchived,
    ),
    queryFn: ({ pageParam }) =>
      fetchRuns({ ...params, limit: RUNS_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: Run[], allPages: Run[][]) => {
      if (lastPage.length < RUNS_PAGE_SIZE) return undefined;
      // A service that predates paging ignores limit and offset and answers every request with
      // the newest page. Comparing against every run already loaded — not just the previous
      // page's first row — catches that even when a run created meanwhile shifted the window.
      const alreadyLoaded = new Set(
        allPages.slice(0, -1).flatMap((page) => page.map((run) => run.id)),
      );
      if (
        alreadyLoaded.size > 0 &&
        lastPage.every((run) => alreadyLoaded.has(run.id))
      ) {
        return undefined;
      }
      return allPages.length * RUNS_PAGE_SIZE;
    },
    staleTime: 10_000,
    refetchInterval: (query: { state: { data?: { pages: Run[][] } } }) =>
      (query.state.data?.pages.length ?? 1) > 1 ? false : 5_000,
  });
}

/**
 * Detail options shared by every reader of one run. `pollWhileQueued` is for the run page alone: a
 * waiting run's place in the line moves when other runs are promoted, and no SSE event fires for
 * that, so the page polls at the list's cadence until a worker picks the run up (MEDA-798). The
 * regression rows, chat cards and cursor reads leave it off, or a page of queued runs would start
 * one request per run every five seconds.
 */
export function runDetailQueryOptions(
  id: string | undefined,
  options: RunDetailReadOptions = {},
  { pollWhileQueued = false }: { pollWhileQueued?: boolean } = {},
) {
  return queryOptions({
    queryKey: [...qualityKeys.run(id ?? ''), options] as const,
    queryFn: () => fetchRunDetail(id as string, options),
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 5_000,
    ...(pollWhileQueued
      ? {
          refetchInterval: (query: { state: { data?: RunDetail } }) =>
            query.state.data?.run.queue_wait ? 5_000 : false,
        }
      : {}),
  });
}

/**
 * Fetch the native JSON report bundle once a run has a combined report. A 404
 * is an absent optional document; other read failures reject the query so a
 * partial canonical bundle cannot be used for report actions.
 */
export function reportBundleQueryOptions(
  id: string,
  enabled: boolean,
  expectedRevision?: number | null,
) {
  return queryOptions({
    queryKey: qualityKeys.reportBundle(id, expectedRevision),
    enabled,
    staleTime: 60_000,
    queryFn: () => fetchReportBundle(id, expectedRevision),
  });
}

/** Regression jobs for one project. Gated on a concrete `projectId` — the list contract requires it,
 *  so the "All projects" state (undefined) shows a pick-a-project prompt instead of querying. */
export function regressionJobsQueryOptions(projectId: string | undefined) {
  return queryOptions({
    queryKey: qualityKeys.regressionJobs(projectId),
    queryFn: () => fetchRegressionJobs(projectId as string),
    enabled: typeof projectId === 'string' && projectId.length > 0,
    staleTime: 15_000,
  });
}

export function regressionJobQueryOptions(id: string) {
  return queryOptions({
    queryKey: qualityKeys.regressionJob(id),
    queryFn: () => fetchRegressionJob(id),
    enabled: id.length > 0,
    staleTime: 15_000,
  });
}

/** A job's run history for the detail view. Gated on a concrete job id. */
export function regressionJobRunsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: qualityKeys.regressionJobRuns(id ?? ''),
    queryFn: () => fetchRegressionJobRuns(id as string),
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 15_000,
  });
}

/**
 * Launch-preflight data for the proposed source and execution settings. An undefined body disables
 * the query. Canonical external-source launches require a matching successful preview; callers
 * handle refresh failures explicitly. `settingsUpdatedAt` participates in the query key so project
 * environment and shared-variable edits invalidate cached previews.
 */
export function runPreflightQueryOptions(
  projectId: string,
  body: RunPreflightBody | undefined,
  settingsUpdatedAt: string | undefined,
) {
  return queryOptions({
    queryKey: qualityKeys.runPreflight(
      projectId,
      body ?? { source: 'testrail' },
      settingsUpdatedAt,
    ),
    queryFn: () =>
      body === undefined
        ? Promise.reject(
            new Error(
              'runPreflightQueryOptions: queryFn ran without a body (guarded by enabled)',
            ),
          )
        : preflightRun(projectId, body),
    enabled: body !== undefined,
    staleTime: 10_000,
    retry: 0,
  });
}

export function caseAdaptationQueryOptions(
  caseId: string,
  workspaceId: string,
  sourceRunId?: string,
  sourceRefOrEnabled?: SourceRef | boolean,
  enabled = true,
) {
  const sourceRef =
    typeof sourceRefOrEnabled === 'object' ? sourceRefOrEnabled : undefined;
  const resolvedEnabled =
    typeof sourceRefOrEnabled === 'boolean' ? sourceRefOrEnabled : enabled;
  return queryOptions({
    queryKey: qualityKeys.caseAdaptation(
      caseId,
      workspaceId,
      sourceRunId,
      sourceRef,
    ),
    queryFn: () =>
      fetchCaseAdaptation(caseId, workspaceId, sourceRunId, sourceRef),
    enabled: resolvedEnabled && caseId.length > 0 && workspaceId.length > 0,
    staleTime: 10_000,
    retry: false,
  });
}

export function suiteExecutionContextQueryOptions(
  suiteId: string,
  workspaceId: string,
  sourceRefOrEnabled?: SourceRef | boolean,
  contextRef?: AdaptationContextRef | AdaptationContextState | null,
  enabled = true,
) {
  const sourceRef =
    typeof sourceRefOrEnabled === 'object' ? sourceRefOrEnabled : undefined;
  const resolvedEnabled =
    typeof sourceRefOrEnabled === 'boolean' ? sourceRefOrEnabled : enabled;
  return queryOptions({
    queryKey: qualityKeys.suiteExecutionContext(
      suiteId,
      workspaceId,
      sourceRef,
      contextRef,
    ),
    queryFn: () =>
      fetchSuiteExecutionContext(suiteId, workspaceId, sourceRef, contextRef),
    enabled: resolvedEnabled && suiteId.length > 0 && workspaceId.length > 0,
    staleTime: 10_000,
    retry: false,
  });
}

export function runLaunchQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: qualityKeys.runLaunch(id ?? ''),
    queryFn: () => fetchRunLaunch(id as string),
    enabled: Boolean(id),
    refetchInterval: 3_000,
    retry: false,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

export function useCreateRun(): UseMutationResult<Run, Error, CreateRunBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createRun,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useCreateExploreRun(): UseMutationResult<
  Run,
  Error,
  CreateExploreRunBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createExploreRun,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useCreateGenerateRun(): UseMutationResult<
  Run,
  Error,
  CreateGenerateRunInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createGenerateRun,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useCreateCombinedGenerateRun(): UseMutationResult<
  Run,
  Error,
  CreateCombinedGenerateRunInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCombinedGenerateRun,
    onSuccess: (_run, input) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      /** The launch rewrote this user's last request, which the next Generate prefills from: the
       *  endpoint's copy, or the projects list on a service without the endpoint. */
      if (input.projectId)
        void qc.invalidateQueries({
          queryKey: qualityKeys.lastGenerationRequest(input.projectId),
        });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useCreateExploratoryRun(): UseMutationResult<
  Run,
  Error,
  CreateExploratoryRunInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createExploratoryRun,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useReviseExploratoryArtifact(
  artifactId: string,
): UseMutationResult<
  ExploratoryRevision,
  Error,
  Parameters<typeof reviseExploratoryArtifact>[1]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => reviseExploratoryArtifact(artifactId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qualityKeys.exploratoryArtifacts }),
  });
}

export function useConfirmExploratoryPublication(
  artifactId: string,
): UseMutationResult<
  ExploratoryPublicationAttempt,
  Error,
  Parameters<typeof confirmExploratoryPublication>[1]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => confirmExploratoryPublication(artifactId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qualityKeys.exploratoryArtifacts }),
  });
}

export function useRetryExploratoryPublication(artifactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attemptId: string) =>
      retryExploratoryPublication(artifactId, attemptId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qualityKeys.exploratoryArtifacts }),
  });
}

export function useReconcileExploratoryPublication(artifactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      attemptId,
      manualOutcome,
    }: {
      attemptId: string;
      manualOutcome?: ExploratoryManualReconciliation;
    }) => reconcileExploratoryPublication(artifactId, attemptId, manualOutcome),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qualityKeys.exploratoryArtifacts }),
  });
}

export function useApproveRun(
  id: string,
): UseMutationResult<Run, Error, ApproveBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApproveBody) => approveRun(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.run(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.proposals(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useCreateGeneratedCasePublicationBatch(
  runId: string,
): UseMutationResult<
  CreateGeneratedCasePublicationBatchResponse,
  Error,
  CreateGeneratedCasePublicationBatchBody & { proposalId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proposalId, ...body }) =>
      createGeneratedCasePublicationBatch(runId, proposalId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.proposals(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useRetryGeneratedCasePublication(
  runId: string,
): UseMutationResult<
  GeneratedCasePublication,
  Error,
  { proposalId: string; publicationId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      publicationId,
    }: {
      proposalId: string;
      publicationId: string;
    }) => retryGeneratedCasePublication(runId, proposalId, publicationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.proposals(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
    },
  });
}

export function useRequestChanges(
  id: string,
): UseMutationResult<Run, Error, RequestChangesBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RequestChangesBody) => requestChanges(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.run(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.proposals(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useCancelRun(id: string): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelRun(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.run(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

/**
 * Drop the run from the list cache after a successful delete. Do not
 * `removeQueries` the still-mounted detail observer — that recreates the
 * query and GETs the id we just deleted.
 */
export function useDeleteRun(id: string): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteRun(id),
    onSuccess: async () => {
      await qc.cancelQueries({ queryKey: qualityKeys.run(id) });
      await qc.cancelQueries({ queryKey: qualityKeys.reportBundleBase(id) });
      // Strip the deleted run out of every cached list immediately — the invalidation below
      // still refetches, but without this the list keeps showing (and linking to) the deleted
      // run until that refetch resolves.
      qc.setQueriesData<Run[] | InfiniteData<Run[]>>(
        { queryKey: qualityKeys.runsBase },
        (cached) => {
          if (!cached) return cached;
          if (Array.isArray(cached)) {
            return cached.filter((run) => run.id !== id);
          }
          return {
            ...cached,
            pages: cached.pages.map((page) =>
              page.filter((run) => run.id !== id),
            ),
          };
        },
      );
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

/** Archive/un-archive a run and refresh every cached runs list so the default (unarchived) board
 *  and any "Show archived" view stay consistent with the new state. */
export function useSetRunArchived(
  id: string,
): UseMutationResult<Run, Error, boolean> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archived: boolean) => setRunArchived(id, archived),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(id) });
    },
  });
}

export function useRestartFailed(
  id: string,
): UseMutationResult<Run, Error, RelaunchRunBody | void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: RelaunchRunBody | void) =>
      restartFailed(id, undefined, settings || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.run(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
    },
  });
}

export function useCorrectOutcome(
  runId: string,
  caseId: string,
): UseMutationResult<
  OutcomeCorrectionReceipt,
  QualityApiError,
  OutcomeCorrectionBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => correctOutcome(runId, caseId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      void qc.invalidateQueries({
        queryKey: qualityKeys.reportBundleBase(runId),
      });
    },
    onError: (error) => {
      if (error.status === 409) {
        void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
        void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
        void qc.invalidateQueries({
          queryKey: qualityKeys.reportBundleBase(runId),
        });
      }
    },
  });
}

// ── Refine flow hooks (MEDA-352) ─────────────────────────────────────────────

/** Refine the given flagged cases. Live progress rides the `revision.updated` SSE invalidation. */
export function useRefineCases(
  id: string,
): UseMutationResult<CaseRevision[], Error, string[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseIds: string[]) => refineCases(id, caseIds),
    onSuccess: (created) => {
      // Seed the cache with the returned pending revisions so the rows flip to "Refining…" and the
      // pending poll starts at once. Without this, between the mutation resolving (isPending → false)
      // and the invalidation refetch landing, the cases look refinable again — a window for a
      // duplicate refine or a stuck state if the refetch is slow or fails.
      qc.setQueryData<CaseRevision[]>(qualityKeys.caseRevisions(id), (prev) => {
        const byCase = new Map(
          (prev ?? []).map((revision) => [revision.case_id, revision]),
        );
        for (const revision of created) byCase.set(revision.case_id, revision);
        return [...byCase.values()];
      });
      void qc.invalidateQueries({ queryKey: qualityKeys.caseRevisions(id) });
    },
  });
}

export function useApplyCaseRevision(
  id: string,
): UseMutationResult<
  ApplyRevisionResult,
  Error,
  { revisionId: string; editedSpec?: CaseSpec }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ revisionId, editedSpec }) =>
      applyCaseRevision(id, revisionId, editedSpec),
    onSuccess: (_result, { revisionId, editedSpec }) => {
      // Reflect the terminal status in the cache at once (apply returns the write result, not the
      // revision) so the closed dialog's row can't be re-opened and the action fired again before
      // the refetch lands. An edited spec is also folded in so the applied row shows the final text.
      qc.setQueryData<CaseRevision[]>(qualityKeys.caseRevisions(id), (prev) =>
        (prev ?? []).map((revision) =>
          revision.id === revisionId
            ? {
                ...revision,
                status: 'applied',
                ...(editedSpec ? { proposed_spec: editedSpec } : {}),
              }
            : revision,
        ),
      );
      void qc.invalidateQueries({ queryKey: qualityKeys.caseRevisions(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.poorlyDescribed(id) });
    },
  });
}

export function useDismissCaseRevision(
  id: string,
): UseMutationResult<CaseRevision, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revisionId: string) => dismissCaseRevision(id, revisionId),
    onSuccess: (updated) => {
      // Dismiss returns the updated revision — seed it so the row leaves `proposed` immediately and
      // the dialog can't be re-opened to dismiss twice before the refetch lands.
      qc.setQueryData<CaseRevision[]>(qualityKeys.caseRevisions(id), (prev) =>
        (prev ?? []).map((revision) =>
          revision.id === updated.id ? updated : revision,
        ),
      );
      void qc.invalidateQueries({ queryKey: qualityKeys.caseRevisions(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.poorlyDescribed(id) });
    },
  });
}

/** Re-run only the refined cases (scoped restart-failed). Mints a new run, like useRerunRun. */
export function useRestartRefined(
  id: string,
): UseMutationResult<Run, Error, string[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseIds: string[]) => restartFailed(id, caseIds),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(created.id) });
      void qc.invalidateQueries({
        queryKey: qualityKeys.reportBundle(created.id),
      });
    },
  });
}

/** Answer a pending input request. `requestId` is passed per-call (not fixed at hook creation) since
 *  a run can carry several pending requests at once. */
export function useAnswerInput(
  runId: string,
): UseMutationResult<
  { state: string },
  Error,
  { requestId: string; body: AnswerInputBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }) => answerInput(runId, requestId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.inputs(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
    },
  });
}

export function useAnswerAuthFlight(
  runId: string,
): UseMutationResult<
  { state: AuthFlightState },
  Error,
  { flightId: string; body: AnswerAuthFlightBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flightId, body }) => answerAuthFlight(runId, flightId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.authFlights(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.inputs(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
    },
  });
}

export function useResetAuthFlightEnvironment(
  runId: string,
): UseMutationResult<
  { state: AuthFlightState; cleanup_status: 'complete' | 'pending' },
  Error,
  ResetAuthFlightBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => resetAuthFlightEnvironment(runId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.authFlights(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.inputs(runId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(runId) });
    },
  });
}

export function useRerunRun(
  id: string,
): UseMutationResult<Run, Error, RelaunchRunBody | void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: RelaunchRunBody | void) =>
      rerunRun(id, settings || undefined),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      // A rerun mints a NEW run, so warm/refresh THAT run's detail + report (not the old one's).
      void qc.invalidateQueries({ queryKey: qualityKeys.run(created.id) });
      void qc.invalidateQueries({
        queryKey: qualityKeys.reportBundle(created.id),
      });
    },
  });
}

export function useRerunCurrentRun(
  id: string,
): UseMutationResult<Run, Error, RerunCurrentBody | void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RerunCurrentBody | void) =>
      rerunCurrentRun(id, body || {}),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      void qc.invalidateQueries({ queryKey: qualityKeys.run(created.id) });
      void qc.invalidateQueries({
        queryKey: qualityKeys.reportBundle(created.id),
      });
    },
  });
}

export function useUpdateProject(
  id: string,
): UseMutationResult<Project, Error, UpdateProjectBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProjectBody) => updateProject(id, body),
    onSuccess: (updated) => {
      qc.setQueryData(qualityKeys.project(id), updated);
      void qc.invalidateQueries({ queryKey: qualityKeys.project(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useSendTeamsTest(
  id: string,
): UseMutationResult<{ ok: true }, Error, string | undefined> {
  return useMutation({
    mutationFn: (webhookUrl?: string) => sendTeamsTest(id, webhookUrl),
  });
}

export function useSendReportPortalTest(
  id: string,
): UseMutationResult<ReportPortalTestResult, Error, void> {
  return useMutation({
    mutationFn: () => sendReportPortalTest(id),
  });
}

export function useAddSourceLink(
  id: string,
): UseMutationResult<Project, Error, SourceLinkBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceLinkBody) => addSourceLink(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useDeleteSourceLink(
  id: string,
): UseMutationResult<Project, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => deleteSourceLink(id, linkId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(id) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useCreateProjectEnvironment(
  projectId: string,
): UseMutationResult<Project, Error, CreateEnvironmentBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEnvironmentBody) =>
      createProjectEnvironment(projectId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useUpdateProjectEnvironment(
  projectId: string,
): UseMutationResult<
  Project,
  Error,
  { name: string; body: UpdateEnvironmentBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      body,
    }: {
      name: string;
      body: UpdateEnvironmentBody;
    }) => updateProjectEnvironment(projectId, name, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useDeleteProjectEnvironment(
  projectId: string,
): UseMutationResult<Project, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => deleteProjectEnvironment(projectId, name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function usePutSharedFileVar(
  projectId: string,
): UseMutationResult<Project, Error, { key: string; file: File }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, file }) => putSharedFileVar(projectId, key, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useClearSharedFileVar(
  projectId: string,
): UseMutationResult<Project, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => clearSharedFileVar(projectId, key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function usePutEnvFileVar(
  projectId: string,
): UseMutationResult<
  Project,
  Error,
  { envName: string; key: string; file: File }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ envName, key, file }) =>
      putEnvFileVar(projectId, envName, key, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useClearEnvFileVar(
  projectId: string,
): UseMutationResult<Project, Error, { envName: string; key: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ envName, key }) => clearEnvFileVar(projectId, envName, key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.project(projectId) });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
    },
  });
}

export function useUpdateProjectGitHubSource(
  projectId: string,
): UseMutationResult<Project, Error, GitHubSourceBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GitHubSourceBody) =>
      updateProjectGitHubSource(projectId, body),
    onSuccess: () => {
      // The projects list carries each project's accounts (admin payload); refresh it so the panel
      // reflects the saved repos + token-presence flag.
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
      // The branch/folder pickers are cached per repo and don't key on token presence, so refresh
      // them too — otherwise saving a token wouldn't refetch a previously-empty picker for up to the
      // stale time.
      void qc.invalidateQueries({ queryKey: ['quality', 'github-folders'] });
      void qc.invalidateQueries({ queryKey: ['quality', 'github-branches'] });
    },
  });
}

// The GitLab twin of useUpdateProjectGitHubSource.
export function useUpdateProjectGitLabSource(
  projectId: string,
): UseMutationResult<Project, Error, GitLabSourceBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GitLabSourceBody) =>
      updateProjectGitLabSource(projectId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
      void qc.invalidateQueries({ queryKey: ['quality', 'gitlab-folders'] });
      void qc.invalidateQueries({ queryKey: ['quality', 'gitlab-branches'] });
    },
  });
}

// ── Regression mutations ───────────────────────────────────────────────────────

export function useCreateRegressionJob(): UseMutationResult<
  RegressionJob,
  Error,
  CreateRegressionJobBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createRegressionJob,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.regressionBase });
    },
  });
}

export function useUpdateRegressionJob(): UseMutationResult<
  RegressionJob,
  Error,
  { id: string; body: UpdateRegressionJobBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRegressionJobBody }) =>
      updateRegressionJob(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.regressionBase });
    },
  });
}

export function useDeleteRegressionJob(): UseMutationResult<
  void,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRegressionJob(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.regressionBase });
    },
  });
}

export function useRetryRegressionSchedule(): UseMutationResult<
  RegressionJob,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => retryRegressionSchedule(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.regressionBase });
    },
  });
}

export function useRunRegressionJobNow(): UseMutationResult<
  { runId: string },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => runRegressionJobNow(id),
    onSuccess: () => {
      // A run-now launches a run AND stamps the job's lastRunId, so refresh both the runs board and
      // the regression list (its last-run link/status).
      void qc.invalidateQueries({ queryKey: qualityKeys.runsBase });
      void qc.invalidateQueries({ queryKey: qualityKeys.regressionBase });
    },
  });
}

/** Suggest a name for a regression job from a prompt. Best-effort — the caller ignores failures and
 *  keeps whatever the user typed. No cache invalidation: this reads nothing the app stores. */
export function useGenerateAgentName(): UseMutationResult<
  string,
  Error,
  string
> {
  return useMutation({
    mutationFn: (prompt: string) => generateAgentName(prompt),
  });
}

export async function readSuiteLaunchSettings(
  sourceRef: import('./quality-types.js').SourceRef,
): Promise<import('./quality-types.js').SuiteLaunchSettings> {
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(sourceRef.projectId)}/suite-launch-settings/read`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ sourceRef }),
    },
  );
  if (!res.ok)
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Could not read suite defaults'),
    );
  return res.json() as Promise<
    import('./quality-types.js').SuiteLaunchSettings
  >;
}
export async function saveSuiteLaunchSettings(
  sourceRef: import('./quality-types.js').SourceRef,
  expectedRevision: number,
  executionMode: import('./quality-types.js').RunExecutionMode | null,
  expectedSourceKey?: string,
): Promise<import('./quality-types.js').SuiteLaunchSettings> {
  await assertCurrentQualityFields('quality.set_suite_launch_settings', [
    'source_ref',
    'expected_revision',
    'execution_mode',
    ...(expectedSourceKey !== undefined ? ['expected_source_key'] : []),
  ]);
  const res = await apiFetch(
    `${API_BASE}/projects/${encodeURIComponent(sourceRef.projectId)}/suite-launch-settings`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify({
        sourceRef,
        expectedRevision,
        executionMode,
        expectedSourceKey,
      }),
    },
  );
  if (!res.ok)
    throw new QualityApiError(
      res.status,
      await errorMessage(res, 'Could not save suite defaults'),
    );
  return res.json() as Promise<
    import('./quality-types.js').SuiteLaunchSettings
  >;
}

/** Catalog resolved by the server under the selected workspace's invocation rules. */
export function fetchQualitySkills(workspaceId: string): Promise<{
  supported: boolean;
  skills: {
    id: string;
    trigger: string;
    title: string;
    description?: string | null;
  }[];
}> {
  return getJson(
    `/skills?workspace_id=${encodeURIComponent(workspaceId)}`,
    'Failed to load Quality skills',
  );
}

export async function generationPlanRequest<T>(
  path: string,
  body?: unknown,
  requestAuthority?: ApiRequestAuthority,
): Promise<T> {
  const authority =
    requestAuthority ??
    (body === undefined ? undefined : captureApiRequestAuthority());
  try {
    const response = await apiFetch(
      `/v1/quality${path}`,
      body === undefined
        ? {}
        : {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
          },
      {},
      authority,
    );
    if (!response.ok) {
      const failure = (await response.json().catch(() => ({}))) as {
        message?: string | string[];
      };
      throw new Error(
        Array.isArray(failure.message)
          ? failure.message.join('; ')
          : (failure.message ??
              'Generation preparation is unavailable. Try again after deployment.'),
      );
    }
    return response.json() as Promise<T>;
  } finally {
    if (!requestAuthority) authority?.release();
  }
}
